import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  CircularProgress,
  LinearProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Grid,
  Alert,
  Snackbar,
  Alert as MuiAlert,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormHelperText,
  Menu,
  ListItemIcon,
  Divider,
  Card,
  CardContent,
  FormControlLabel,
  Tabs,
  Tab,
  TableSortLabel,
  Link,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Schedule,
  Publish,
  Facebook,
  Instagram,
  LinkedIn,
  Refresh,
  Pause,
  PlayArrow,
  Repeat,
  Preview,
  CalendarToday,
  AccessTime,
  Autorenew,
  BarChart,
  Share,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  postsAPI,
  clientsAPI,
  platformAccountsAPI,
  mediaAPI,
  schedulingAPI,
  groupSharingAPI,
  pinterestAPI,
  publicFileUrl,
} from '../services/api';
import {
  MEDIA_CONTENT_PILLAR_LABELS,
  MEDIA_CONTENT_PILLAR_UPLOAD_OPTIONS,
} from '../config/contentPillars';
import { formatInTimeZone } from 'date-fns-tz';
import PostPreview from '../components/PostPreview';
import EngagementPredictionPanel from '../components/EngagementPredictionPanel';
import PrePublishScorePanel from '../components/PrePublishScorePanel';
import PostCaptionField from './posts/PostCaptionField';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useClients } from '../contexts/ClientContext';
import { useScopedClientSelection } from '../hooks/useScopedClientSelection';
import ConfirmDialog from '../components/ConfirmDialog';
import PageLoadingState from '../components/PageLoadingState';
import PageErrorAlert from '../components/PageErrorAlert';
import { DataTable } from '../components/primitives';
import { parseClientIdParam } from '../utils/lastSelectedClientStorage';
import { usePublishStatusPolling } from '../hooks/usePublishStatusPolling';
import {
  formatApiError,
  getPostPublishError,
  isPostPublishInFlight,
} from '../utils/postPublishErrors';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TodayIcon from '@mui/icons-material/Today';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DraftsIcon from '@mui/icons-material/Drafts';
import GetApp from '@mui/icons-material/GetApp';
import CheckCircle from '@mui/icons-material/CheckCircle';
import { SHOW_INSTAGRAM_UI, SHOW_INSTAGRAM_AS_PUBLISH_TARGET } from '../config/featureFlags';

import {
  postsDebug,
  parseMediaUrlsField,
  parsePostTimeMs,
  comparePostDates,
  formatToEST,
} from './posts/postUtils';
import {
  contentTypes,
  PLATFORM_OPTIONS,
  PLATFORM_PERF_IDS,
  initialForm,
  excludeInstagramFromOptions,
  GROUP_SHARE_TEXT_MAX_LENGTH,
} from './posts/constants';
import {
  connectedPlatformOptions,
  defaultConnectedPlatforms,
  normalizePublishPlatformSlug,
} from '../utils/connectedPlatforms';
import {
  PublishStatusChip,
  EngagementSummary,
} from './posts/PostPresentation';
import GroupShareStatus from './posts/GroupShareStatus';
import PostingProgressDialog from './posts/PostingProgressDialog';
import PostsStatsCards from './posts/PostsStatsCards';
import PostsAnalyticsTab from './posts/PostsAnalyticsTab';
import PostTableRow, { getStatusColor, isRecurringPost } from './posts/PostTableRow';
import { useStableCallback } from '../hooks/useStableCallback';

export default function Posts() {
  const { user } = useAuth();
  const { showError } = useToast();
  const { clients, loaded: clientsLoaded, setLastSelectedClientId } = useClients();
  const { clientId } = useScopedClientSelection();
  const location = useLocation();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [platformAccounts, setPlatformAccounts] = useState([]);
  const [youtubePlaylists, setYoutubePlaylists] = useState([]);
  const [pinterestBoards, setPinterestBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState({
    client_id: '',
    platforms: [],
    content: '',
    content_type: 'text',
    theme: '',
    scheduled_time: '',
    timezone: 'EST',
    title: '',
    video: null,
    image: null,
    group_share_text: '',
    group_share_texts: {},
  });
  // Phase 16.R7: remount caption field with seed; keep latest text without per-keystroke setForm.
  const [captionFieldKey, setCaptionFieldKey] = useState(0);
  const latestCaptionRef = useRef('');
  const [editingId, setEditingId] = useState(null);
  const [viewPost, setViewPost] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [schedulingId, setSchedulingId] = useState(null);
  const [scheduledTime, setScheduledTime] = useState(new Date());
  const [filters, setFilters] = useState({
    client_id: '',
    platform: '',
    status: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [formErrors, setFormErrors] = useState({});
  const [mediaUploading, setMediaUploading] = useState(false);
  const [mediaUploadContentPillar, setMediaUploadContentPillar] =
    useState('motivation');
  const [mediaUploadRightsConfirmed, setMediaUploadRightsConfirmed] =
    useState(false);
  const postFormMediaLibraryClientId = useMemo(() => {
    const raw = form.client_id;
    if (raw === '' || raw == null) return null;
    const n = parseInt(String(raw), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [form.client_id]);

  const themeOptions = useMemo(() => {
    const base = ['light', 'dark', 'black'];
    const c = clients.find((x) => String(x.id) === String(form.client_id));
    const custom = Array.isArray(c?.content_themes) ? c.content_themes : [];
    return [
      ...new Set([
        ...base,
        ...custom
          .map((t) => String(t || '').trim().toLowerCase())
          .filter(Boolean),
      ]),
    ];
  }, [clients, form.client_id]);
  const [postingProgress, setPostingProgress] = useState(null);
  const [platformStatus, setPlatformStatus] = useState({});
  // Add state for selected posts
  const [selectedPosts, setSelectedPosts] = useState([]);
  // New state for enhanced functionality
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPostForMenu, setSelectedPostForMenu] = useState(null);
  const [previewDialog, setPreviewDialog] = useState({
    open: false,
    post: null,
  });
  const [rescheduleDialog, setRescheduleDialog] = useState({
    open: false,
    post: null,
    newTime: new Date(),
  });
  const [recurringDialog, setRecurringDialog] = useState({
    open: false,
    post: null,
  });
  const [bulkActionDialog, setBulkActionDialog] = useState({
    open: false,
    action: null,
  });
  const [recurringConfig, setRecurringConfig] = useState({
    frequency: 'daily',
    interval: 1,
    endDate: null,
    daysOfWeek: [],
    timeOfDay: '09:00',
  });
  const [bulkScheduledTime, setBulkScheduledTime] = useState(new Date());
  const [bulkTimezone, setBulkTimezone] = useState('EST');
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: null,
    data: null,
  });
  const [shareToGroupsPostId, setShareToGroupsPostId] = useState(null);
  const [tab, setTab] = useState(0);
  /** Table tab: filter to one client (empty = all). */
  const [tableClientFilterId, setTableClientFilterId] = useState('');
  /** Table tab: sort field + direction (client-side on loaded posts). */
  const [tableSort, setTableSort] = useState({
    field: 'scheduled',
    direction: 'desc',
  });

  const platformOptions = excludeInstagramFromOptions([
    { value: 'facebook', label: 'Facebook' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'x', label: 'X' },
    { value: 'youtube', label: 'YouTube' },
    { value: 'tumblr', label: 'Tumblr' },
    { value: 'pinterest', label: 'Pinterest' },
  ]);

  const tableViewPosts = useMemo(() => {
    let list = Array.isArray(posts) ? posts : [];
    if (tableClientFilterId !== '') {
      const cid = parseInt(tableClientFilterId, 10);
      if (Number.isFinite(cid)) {
        list = list.filter((p) => p.client_id === cid);
      }
    }
    const { field, direction } = tableSort;
    return [...list].sort((a, b) => {
      if (field === 'title') {
        const ta = (a.title || 'Untitled').toLowerCase();
        const tb = (b.title || 'Untitled').toLowerCase();
        const cmp = ta.localeCompare(tb, undefined, { sensitivity: 'base' });
        return direction === 'asc' ? cmp : -cmp;
      }
      if (field === 'client') {
        const ca = (a.client?.name || 'N/A').toLowerCase();
        const cb = (b.client?.name || 'N/A').toLowerCase();
        const cmp = ca.localeCompare(cb, undefined, { sensitivity: 'base' });
        return direction === 'asc' ? cmp : -cmp;
      }
      if (field === 'scheduled') {
        return comparePostDates(
          parsePostTimeMs(a.scheduled_time),
          parsePostTimeMs(b.scheduled_time),
          direction,
        );
      }
      if (field === 'published') {
        return comparePostDates(
          parsePostTimeMs(a.published_time),
          parsePostTimeMs(b.published_time),
          direction,
        );
      }
      return 0;
    });
  }, [posts, tableClientFilterId, tableSort]);

  const selectedPostIds = useMemo(() => new Set(selectedPosts), [selectedPosts]);

  const tableSelectedCount = useMemo(
    () => tableViewPosts.filter((p) => selectedPosts.includes(p.id)).length,
    [tableViewPosts, selectedPosts],
  );

  const handleTableSort = (field) => {
    setTableSort((prev) => {
      if (prev.field === field) {
        return { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return {
        field,
        direction: field === 'title' || field === 'client' ? 'asc' : 'desc',
      };
    });
  };

  const fetchPosts = useCallback(
    async (options = {}) => {
      if (!options.silent) setLoading(true);
      try {
        const data = await postsAPI.getPosts(filters);
        setPosts(Array.isArray(data) ? data : []);
        if (!options.silent) setError(null);
      } catch (err) {
        setPosts([]);
        const msg = formatApiError(err, 'Failed to load posts');
        setError(msg);
        if (options.userInitiated) showError(msg);
      } finally {
        if (!options.silent) setLoading(false);
      }
    },
    [filters, showError],
  );

  usePublishStatusPolling({
    posts,
    refreshPosts: fetchPosts,
    showError,
  });

  const fetchPlatformAccounts = async () => {
    try {
      const data = await platformAccountsAPI.getPlatformAccounts();
      setPlatformAccounts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load platform accounts:', err);
      showError(formatApiError(err, 'Failed to load platform accounts'));
      setPlatformAccounts([]);
    }
  };

  const resolvePlatformAccount = useCallback(
    async (clientId, platform) => {
      if (!clientId || !platform) return null;
      const normalizedClientId = String(clientId);
      const normalizedPlatform = normalizePublishPlatformSlug(platform);
      const accountMatches = (account) => {
        const accountSlug = normalizePublishPlatformSlug(account.platform);
        const platformMatches = accountSlug === normalizedPlatform;
        const clientMatches =
          account.client_id == null ||
          String(account.client_id) === normalizedClientId;
        return platformMatches && clientMatches;
      };

      const cachedAccount = platformAccounts.find(accountMatches);
      if (cachedAccount) return cachedAccount;

      try {
        const fetchedAccounts =
          await platformAccountsAPI.getPlatformAccounts(clientId);
        const clientAccounts = Array.isArray(fetchedAccounts)
          ? fetchedAccounts
          : [];
        return clientAccounts.find(accountMatches) || null;
      } catch (error) {
        console.error(
          `Error fetching platform account for ${platform}:`,
          error,
        );
        showError(
          formatApiError(
            error,
            `Failed to load ${platform} account for this client`,
          ),
        );
        return null;
      }
    },
    [platformAccounts, showError],
  );

  const formPlatformOptions = useMemo(
    () => connectedPlatformOptions(platformAccounts, form.client_id),
    [platformAccounts, form.client_id],
  );

  useEffect(() => {
    fetchPosts();
  }, [filters, fetchPosts]);

  // Platform accounts are not filtered by the posts filters, so fetch them once on mount
  // instead of on every filter change (16.R4).
  useEffect(() => {
    fetchPlatformAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const clientIdParam = urlParams.get('client_id');
    const platform = urlParams.get('platform');

    if (clientIdParam) {
      const parsed = parseClientIdParam(clientIdParam);
      if (parsed) setLastSelectedClientId(parsed);
      setForm((prev) => ({
        ...prev,
        client_id: clientIdParam,
        ...(platform ? { platforms: [platform.toLowerCase()] } : {}),
      }));
      setTableClientFilterId(clientIdParam);
      return;
    }

    if (!clientsLoaded || !clientId) return;
    const idStr = String(clientId);
    setForm((prev) => (prev.client_id ? prev : { ...prev, client_id: idStr }));
    setTableClientFilterId((prev) => (prev !== '' ? prev : idStr));
  }, [location.search, clientsLoaded, clientId, setLastSelectedClientId]);

  useEffect(() => {
    const parsed = parseClientIdParam(form.client_id);
    if (parsed) setLastSelectedClientId(parsed);
  }, [form.client_id, setLastSelectedClientId]);

  useEffect(() => {
    const parsed = parseClientIdParam(tableClientFilterId);
    if (parsed) setLastSelectedClientId(parsed);
  }, [tableClientFilterId, setLastSelectedClientId]);

  const stats = useMemo(() => {
    // Calculate stats from posts (guard: posts may not be array if API shape changes)
    const postList = Array.isArray(posts) ? posts : [];
    const scheduled = postList.filter((p) => p.status === 'scheduled').length;
    const published = postList.filter((p) => p.status === 'published').length;
    const failed = postList.filter((p) => p.status === 'failed').length;
    const drafts = postList.filter((p) => p.status === 'draft').length;
    const recurring = postList.filter(
      (p) => p.recurring_schedule_id || p.is_recurring,
    ).length;
    // Today/Week
    const today = postList.filter((p) => {
      if (!p.scheduled_time) return false;
      const d = new Date(p.scheduled_time);
      const now = new Date();
      return d.toDateString() === now.toDateString();
    }).length;
    const week = postList.filter((p) => {
      if (!p.scheduled_time) return false;
      const d = new Date(p.scheduled_time);
      const now = new Date();
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      return d >= weekStart && d <= weekEnd;
    }).length;
    // Engagement (avg likes+comments+shares for published)
    const publishedPosts = postList.filter((p) => p.status === 'published');
    const totalEngagement = publishedPosts.reduce((sum, p) => {
      const m = p.engagement_metrics || {};
      return sum + (m.likes || 0) + (m.comments || 0) + (m.shares || 0);
    }, 0);
    const engagement =
      publishedPosts.length > 0
        ? Math.round(totalEngagement / publishedPosts.length)
        : 0;
    return {
      scheduled,
      published,
      today,
      week,
      failed,
      recurring,
      drafts,
      engagement,
    };
  }, [posts]);

  // Pre-fill form when coming from chatbot "Create post" (caption-from-media)
  useEffect(() => {
    const draft = location.state?.draft;
    if (location.state?.fromChatbot && draft) {
      const baseUrl =
        process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const uploadsBase = baseUrl.replace('/api/v1', '');
      const mediaUrl = draft.media_url
        ? draft.media_url.startsWith('http')
          ? draft.media_url
          : `${uploadsBase}${draft.media_url}`
        : '';
      const nextContent = draft.content || '';
      latestCaptionRef.current = nextContent;
      setForm((prev) => ({
        ...prev,
        title: draft.title || prev.title,
        content: draft.content || prev.content,
        hashtags: draft.hashtags || prev.hashtags,
        media_urls: mediaUrl || prev.media_urls,
      }));
      setCaptionFieldKey((k) => k + 1);
      setOpenForm(true);
      setEditingId(null);
      navigate('/posts', { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const fetchYoutubePlaylists = async (clientId) => {
    try {
      const data = await platformAccountsAPI.getYouTubePlaylists(clientId);
      setYoutubePlaylists(data.playlists || []);
    } catch (error) {
      console.error('Error fetching YouTube playlists:', error);
      showError(formatApiError(error, 'Failed to load YouTube playlists'));
      setYoutubePlaylists([]);
    }
  };

  const fetchPinterestBoards = async (clientId) => {
    try {
      const boards = await pinterestAPI.getPinterestBoards(clientId);
      setPinterestBoards(boards || []);
    } catch (error) {
      console.error('Error fetching Pinterest boards:', error);
      showError(formatApiError(error, 'Failed to load Pinterest boards'));
      setPinterestBoards([]);
    }
  };

  const handleOpenForm = useStableCallback((post = null) => {
    if (post) {
      // Convert post data to form format
      const pinPs = post.platform_specific?.pinterest || {};
      const formData = {
        ...post,
        platforms: post.platform ? [post.platform] : [], // Convert single platform to array
        // Ensure all required form fields exist
        client_id: post.client_id || '',
        content: post.content || '',
        content_type: post.content_type || 'text',
        theme: post.theme || '',
        scheduled_time: post.scheduled_time || '',
        timezone: 'EST',
        title: post.title || '',
        video: post.video || null,
        image: post.image || null,
        group_share_text: post.group_share_text || '',
        group_share_texts: post.group_share_texts || {},
        hashtags: Array.isArray(post.hashtags)
          ? post.hashtags.join(', ')
          : post.hashtags || '',
        media_urls: Array.isArray(post.media_urls)
          ? post.media_urls.join(', ')
          : post.media_urls || '',
        pinterestLink: pinPs.link_url || '',
        pinterestBoard: pinPs.board_id || pinPs.board_name || '',
      };
      latestCaptionRef.current = formData.content || '';
      setForm(formData);
      setEditingId(post.id);
      setCaptionFieldKey((k) => k + 1);

      // Fetch YouTube playlists if client has YouTube
      if (post.client_id) {
        fetchYoutubePlaylists(post.client_id);
      }
    } else {
      latestCaptionRef.current = '';
      setForm(initialForm);
      setEditingId(null);
      setCaptionFieldKey((k) => k + 1);
    }
    setOpenForm(true);
  });

  const handleCloseForm = () => {
    setOpenForm(false);
    latestCaptionRef.current = '';
    setForm(initialForm);
    setEditingId(null);
  };

  const commitCaption = useCallback((value) => {
    const next = value == null ? '' : String(value);
    latestCaptionRef.current = next;
    setForm((prev) => (prev.content === next ? prev : { ...prev, content: next }));
  }, []);

  const syncCaptionLive = useCallback((value) => {
    latestCaptionRef.current = value == null ? '' : String(value);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const scheduledTimeISO = useMemo(() => {
    if (form.scheduled_time) {
      const d = new Date(form.scheduled_time);
      if (!Number.isNaN(d.getTime())) return d.toISOString();
    }
    if (scheduledTime instanceof Date && !Number.isNaN(scheduledTime.getTime())) {
      return scheduledTime.toISOString();
    }
    return new Date().toISOString();
  }, [form.scheduled_time, scheduledTime]);

  // In handlePlatformChange, always filter out empty strings
  const handlePlatformChange = (event) => {
    const {
      target: { value },
    } = event;
    const platforms = typeof value === 'string' ? value.split(',') : value;
    const filteredPlatforms = platforms
      .map((p) => p && p.trim())
      .filter(Boolean);
    postsDebug('Platform change:', { value, platforms, filteredPlatforms });
    setForm({
      ...form,
      platforms: filteredPlatforms,
    });
  };

  const validateForm = () => {
    const errors = {};
    const content = latestCaptionRef.current || form.content;
    const hasMediaUrls =
      (typeof form.media_urls === 'string' && (form.media_urls || '').trim()) ||
      (Array.isArray(form.media_urls) && form.media_urls.length > 0);
    if ((form.platforms || []).length === 0)
      errors.platforms = 'Select at least one platform.';
    if ((form.platforms || []).includes('youtube')) {
      if (!form.title) errors.title = 'YouTube requires a title.';
      if (!form.video && !hasMediaUrls) errors.video = 'YouTube requires a video file or uploaded media.';
    }
    if ((form.platforms || []).includes('facebook')) {
      if (!content && !form.image && !form.video && !hasMediaUrls)
        errors.content = 'Facebook requires text, image, or video.';
    }
    if ((form.platforms || []).includes('pinterest')) {
      if (!hasMediaUrls) errors.media_urls = 'Pinterest requires an image.';
    }
    return errors;
  };

  const handleFileChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.files[0] });
  };

  const handleMediaUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    const clientIdRaw = form.client_id;
    const clientId =
      clientIdRaw !== '' && clientIdRaw != null
        ? parseInt(String(clientIdRaw), 10)
        : NaN;

    if (Number.isFinite(clientId) && clientId > 0 && !mediaUploadRightsConfirmed) {
      setSnackbar({
        open: true,
        message:
          'Check "I have the rights to use this asset." before uploading to the media library.',
        severity: 'error',
      });
      event.target.value = '';
      return;
    }

    setMediaUploading(true);
    try {
      let newUrls;
      if (Number.isFinite(clientId) && clientId > 0) {
        const results = await Promise.all(
          files.map((file) =>
            clientsAPI.uploadClientMedia(clientId, file, {
              source: 'upload',
              content_pillar: mediaUploadContentPillar,
              rights_confirmed: true,
            }),
          ),
        );
        newUrls = results.map((r) => r.file_url).filter(Boolean);
      } else {
        const mediaResults = await Promise.all(
          files.map((file) => mediaAPI.upload(file)),
        );
        newUrls = mediaResults.map((media) => media.url);
      }

      setForm((prev) => {
        const existing = parseMediaUrlsField(prev.media_urls);
        const merged = [...existing, ...newUrls];
        return { ...prev, media_urls: merged.join(', ') };
      });

      setSnackbar({
        open: true,
        message:
          Number.isFinite(clientId) && clientId > 0
            ? `${files.length} file(s) added to this client’s gallery and attached to the post.`
            : `${files.length} file(s) uploaded. Select a client first to save uploads to the media library.`,
        severity: 'success',
      });
    } catch (err) {
      console.error('Media upload error:', err);
      showError(formatApiError(err, 'Some files failed to upload'));
      setSnackbar({
        open: true,
        message: 'Some files failed to upload. Please try again.',
        severity: 'error',
      });
    } finally {
      setMediaUploading(false);
      // Reset the file input
      event.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const caption = latestCaptionRef.current || form.content;
    if (caption !== form.content) {
      setForm((prev) => ({ ...prev, content: caption }));
    }
    const errors = validateForm();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;
    try {
      // Clean up form data before sending
      const platforms = (form.platforms || []).filter(
        (p) => p && p.trim() !== '',
      );
      if (platforms.length === 0) {
        setSnackbar({
          open: true,
          message: 'Select at least one platform.',
          severity: 'error',
        });
        return;
      }
      let allSucceeded = true;

      // Check if we're editing an existing post
      if (editingId) {
        // Update existing post
        postsDebug(`Updating post ${editingId}`);
        const payload = {
          client_id: form.client_id,
          content: caption,
          content_type: form.content_type,
          theme: form.theme || null,
          title: form.title,
          status: 'draft',
          platform: platforms[0], // For editing, use the first platform
          target_platforms: platforms,
        };
        appendGroupShareText(payload, platforms);

        const platformAccount = await resolvePlatformAccount(
          form.client_id,
          platforms[0],
        );
        if (platformAccount) {
          payload.platform_account_id = platformAccount.id;
        }

        // Add media and hashtags
        if (form.media_urls) {
          if (typeof form.media_urls === 'string' && form.media_urls.trim()) {
            payload.media_urls = form.media_urls
              .split(',')
              .map((url) => url.trim())
              .filter(Boolean);
          } else if (
            Array.isArray(form.media_urls) &&
            form.media_urls.length > 0
          ) {
            payload.media_urls = form.media_urls.filter(Boolean);
          }
        }
        if (form.hashtags && form.hashtags.trim()) {
          payload.hashtags = form.hashtags
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean);
        }

        try {
          const response = await postsAPI.updatePost(editingId, payload);
          postsDebug(`Post updated:`, response);
        } catch (error) {
          console.error(`Error updating post:`, error);
          showError(formatApiError(error, 'Failed to update post'));
          allSucceeded = false;
        }
      } else {
        // Create new posts for each platform
        for (const platform of platforms) {
          postsDebug(`Creating post for platform: ${platform}`);
          postsDebug(`Form data:`, form);

          const payload = {
            client_id: form.client_id,
            content: caption,
            content_type: form.content_type,
            theme: form.theme || null,
            title: form.title,
            status: 'draft',
            platform,
            target_platforms: platforms,
          };
          appendGroupShareText(payload, platforms);

          postsDebug(`Initial payload:`, payload);

          const platformAccount = await resolvePlatformAccount(
            form.client_id,
            platform,
          );
          if (platformAccount) {
            payload.platform_account_id = platformAccount.id;
          } else {
            postsDebug(
              `No platform account found for client ${form.client_id} and ${platform}`,
            );
          }

          if (form.media_urls) {
            if (typeof form.media_urls === 'string' && form.media_urls.trim()) {
              payload.media_urls = form.media_urls
                .split(',')
                .map((url) => url.trim())
                .filter(Boolean);
            } else if (
              Array.isArray(form.media_urls) &&
              form.media_urls.length > 0
            ) {
              payload.media_urls = form.media_urls.filter(Boolean);
            }
          }
          if (form.hashtags && form.hashtags.trim()) {
            payload.hashtags = form.hashtags
              .split(',')
              .map((tag) => tag.trim())
              .filter(Boolean);
          }

          try {
            const response = await postsAPI.createPost(payload);
            postsDebug(`${platform} post created:`, response);
            // Posts are now saved as drafts by default - no automatic publishing
          } catch (error) {
            console.error(`Error creating ${platform} post:`, error);
            showError(formatApiError(error, `Failed to create ${platform} post`));
            allSucceeded = false;
          }
        }
      }

      if (allSucceeded) {
        setSnackbar({
          open: true,
          message: editingId
            ? 'Post updated successfully!'
            : 'All posts saved as drafts successfully!',
          severity: 'success',
        });
        handleCloseForm();
        fetchPosts();
      } else {
        setSnackbar({
          open: true,
          message: 'Some posts failed to create. Review the toast errors and try again.',
          severity: 'warning',
        });
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      showError(formatApiError(error, 'Error creating posts'));
      setSnackbar({
        open: true,
        message: 'Error creating posts.',
        severity: 'error',
      });
    }
  };

  const handleDelete = async (id) => {
    setConfirmDialog({
      open: true,
      action: 'delete',
      data: { id, type: 'single' },
    });
  };

  const handleConfirmAction = async () => {
    const { action, data } = confirmDialog;

    try {
      switch (action) {
        case 'delete':
          if (data.type === 'single') {
            setDeletingId(data.id);
            await postsAPI.deletePost(data.id);
            setSnackbar({
              open: true,
              message: 'Post deleted successfully!',
              severity: 'success',
            });
          } else if (data.type === 'bulk') {
            for (const id of data.ids) {
              await postsAPI.deletePost(id);
            }
            setSelectedPosts([]);
            setSnackbar({
              open: true,
              message: `${data.ids.length} posts deleted successfully!`,
              severity: 'success',
            });
          }
          break;
        default:
          break;
      }
      fetchPosts();
    } catch (err) {
      const msg = formatApiError(err, 'Failed to perform action');
      showError(msg);
      setSnackbar({
        open: true,
        message: msg,
        severity: 'error',
      });
    } finally {
      setDeletingId(null);
      setConfirmDialog({ open: false, action: null, data: null });
    }
  };

  const handleView = useCallback(async (id) => {
    try {
      const post = await postsAPI.getPost(id);
      setViewPost(post);
    } catch (err) {
      showError(formatApiError(err, 'Failed to load post details'));
    }
  }, [showError]);

  const handleCloseView = () => {
    setViewPost(null);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const postId = urlParams.get('post_id');
    if (!postId) return;
    const id = parseInt(postId, 10);
    if (Number.isFinite(id) && id > 0) {
      handleView(id);
    }
  }, [location.search, handleView]);

  // In handleSchedule, update to handle multiple platforms like Post Now
  const handleSchedule = async (id, forceImmediate = false) => {
    postsDebug('handleSchedule called with id:', id);
    postsDebug('scheduledTime:', scheduledTime);
    try {
      const estTimeZone = 'America/New_York';
      const estISOString = scheduledTime
        ? formatInTimeZone(
            scheduledTime,
            estTimeZone,
            "yyyy-MM-dd'T'HH:mm:ssXXX",
          )
        : null;
      postsDebug('estISOString:', estISOString);

      // Test formatInTimeZone function
      try {
        const testDate = new Date();
        const testFormatted = formatInTimeZone(
          testDate,
          'America/New_York',
          "yyyy-MM-dd'T'HH:mm:ssXXX",
        );
        postsDebug(
          'formatInTimeZone test - input:',
          testDate,
          'output:',
          testFormatted,
        );
      } catch (error) {
        console.error('formatInTimeZone test failed:', error);
      }

      if (id === 'new') {
        // Schedule a new post using form data
        const caption = latestCaptionRef.current || form.content;
        if (caption !== form.content) {
          setForm((prev) => ({ ...prev, content: caption }));
        }
        postsDebug('Scheduling new post, form data:', form);
        postsDebug('Form platforms raw:', form.platforms);
        const platforms = (form.platforms || []).filter(
          (p) => p && p.trim() !== '',
        );
        postsDebug(`Selected platforms:`, platforms);
        if (platforms.length === 0) {
          console.error('No platforms selected');
          showError('Select at least one platform before scheduling.');
          throw new Error('No platform selected');
        }

        // Check if this is immediate posting (no scheduled time or forced immediate)
        const isImmediatePost = forceImmediate || !scheduledTime;
        postsDebug('Immediate post check:', {
          scheduledTime,
          forceImmediate,
          isImmediatePost,
        });

        // Initialize progress tracking for immediate posts
        if (isImmediatePost) {
          postsDebug('Initializing progress tracking for immediate post');
          postsDebug('Platforms for progress:', platforms);
          setPostingProgress(true);
          const initialStatus = {};
          platforms.forEach((platform) => {
            initialStatus[platform] = 'pending';
          });
          setPlatformStatus(initialStatus);

          // Create a mock post object for progress tracking
          setSelectedPostForMenu({
            id: 'new',
            platforms: platforms,
            content: caption,
            title: form.title,
          });
          postsDebug('Progress tracking initialized');
        }

        let allSucceeded = true;
        let lastInstagramStatus = null; // 'both' | reason string when FB publish includes IG info
        for (const platform of platforms) {
          const postData = {
            client_id: form.client_id,
            content: caption,
            content_type: form.content_type,
            theme: form.theme || null,
            title: form.title,
            status: isImmediatePost ? 'published' : 'scheduled',
            platform,
            target_platforms: platforms,
            scheduled_time: isImmediatePost ? null : estISOString,
          };
          appendGroupShareText(postData, platforms);

          const platformAccount = await resolvePlatformAccount(
            form.client_id,
            platform,
          );
          if (platformAccount) {
            postData.platform_account_id = platformAccount.id;
          }

          if (form.media_urls && form.media_urls.length > 0) {
            postData.media_urls = Array.isArray(form.media_urls)
              ? form.media_urls
              : form.media_urls
                  .split(',')
                  .map((url) => url.trim())
                  .filter(Boolean);
          }
          if (form.hashtags && form.hashtags.length > 0) {
            postData.hashtags = Array.isArray(form.hashtags)
              ? form.hashtags
              : form.hashtags
                  .split(',')
                  .map((tag) => tag.trim())
                  .filter(Boolean);
          }

          // Add YouTube metadata if platform is YouTube
          if (platform.toLowerCase() === 'youtube') {
            const youtubeMetadata = {};

            if (form.youtubeTitle) {
              youtubeMetadata.title = form.youtubeTitle;
            }
            if (form.youtubeDescription) {
              youtubeMetadata.description = form.youtubeDescription;
            }
            if (form.youtubeTags) {
              youtubeMetadata.tags = form.youtubeTags
                .split(',')
                .map((tag) => tag.trim())
                .filter(Boolean);
            }
            if (form.youtubePrivacy) {
              youtubeMetadata.privacyStatus = form.youtubePrivacy;
            }
            if (form.youtubeCategory) {
              youtubeMetadata.categoryId = form.youtubeCategory;
            }
            if (form.youtubePlaylists && form.youtubePlaylists.length > 0) {
              youtubeMetadata.playlistIds = form.youtubePlaylists;
            }

            if (Object.keys(youtubeMetadata).length > 0) {
              postData.platform_specific = {
                ...(postData.platform_specific || {}),
                youtube: youtubeMetadata,
              };
            }
          }

          // Add Pinterest metadata if platform is Pinterest
          if (platform.toLowerCase() === 'pinterest') {
            const pinterestMetadata = {};
            if (form.pinterestLink) pinterestMetadata.link_url = form.pinterestLink;
            if (form.pinterestBoard) pinterestMetadata.board_name = form.pinterestBoard.trim();
            if (Object.keys(pinterestMetadata).length > 0) {
              postData.platform_specific = {
                ...(postData.platform_specific || {}),
                pinterest: pinterestMetadata,
              };
            }
          }

          try {
            // Update progress for immediate posts
            if (isImmediatePost) {
              setPlatformStatus((prev) => ({
                ...prev,
                [platform]: 'processing',
              }));
            }

            let response;
            if (isImmediatePost) {
              // For immediate posting: create as draft, then publish
              const draftData = { ...postData, status: 'draft' };
              const created = await postsAPI.createPost(draftData);
              postsDebug(`${platform} post created as draft:`, created);

              // Now publish it immediately
              response = await postsAPI.publishPost(created.id);
              postsDebug(`${platform} post published:`, response);
              postsDebug(
                `${platform} response type:`,
                typeof response,
                'keys:',
                Object.keys(response || {}),
              );
            } else {
              // For scheduled posting: use the existing flow
              response = await postsAPI.createAndSchedulePost(postData);
              postsDebug(`${platform} post scheduled:`, response);
            }

            // Check if the response is valid (response can be the post object directly or wrapped in data)
            if (
              response &&
              (response.data ||
                response.id ||
                response.status === 'published' ||
                response.message)
            ) {
              // Capture Instagram dual-publish info for Facebook (used in snackbar below)
              if (
                SHOW_INSTAGRAM_UI &&
                platform.toLowerCase() === 'facebook' &&
                response.instagram_published !== undefined
              ) {
                lastInstagramStatus = response.instagram_published
                  ? 'both'
                  : response.instagram_reason || 'unknown';
              }
              // Update progress for immediate posts
              if (isImmediatePost) {
                setPlatformStatus((prev) => ({
                  ...prev,
                  [platform]: 'success',
                }));
                // Add delay to show progress
                await new Promise((resolve) => setTimeout(resolve, 500));
              }
            } else {
              // API call failed
              console.error(
                `Invalid response from API for ${platform}:`,
                response,
              );
              showError(`Unexpected API response while processing ${platform}.`);
              throw new Error(
                `Invalid response from API: ${JSON.stringify(response)}`,
              );
            }
          } catch (error) {
            console.error(
              `Error ${
                isImmediatePost ? 'publishing' : 'scheduling'
              } ${platform} post:`,
              error,
            );
            showError(
              formatApiError(
                error,
                `Failed to ${isImmediatePost ? 'publish' : 'schedule'} ${platform} post`,
              ),
            );
            allSucceeded = false;

            // Update progress for immediate posts
            if (isImmediatePost) {
              setPlatformStatus((prev) => ({ ...prev, [platform]: 'error' }));
            }
          }
        }

        if (allSucceeded) {
          let successMsg =
            isImmediatePost
              ? 'All posts published successfully!'
              : 'All posts scheduled successfully!';
          if (isImmediatePost && lastInstagramStatus === 'both') {
            successMsg = 'Posted to Facebook and Instagram!';
          } else if (isImmediatePost && lastInstagramStatus && lastInstagramStatus !== 'both') {
            successMsg = `Posted to Facebook. Instagram: ${lastInstagramStatus}`;
          }
          setSnackbar({
            open: true,
            message: successMsg,
            severity: lastInstagramStatus && lastInstagramStatus !== 'both' ? 'info' : 'success',
          });

          // Close the form and dialog
          handleCloseForm();
          setSchedulingId(null);

          // Refresh posts
          await fetchPosts();
          postsDebug(
            `Posts refreshed after ${
              isImmediatePost ? 'publishing' : 'scheduling'
            } new posts`,
          );
        } else {
          setSnackbar({
            open: true,
            message: isImmediatePost
              ? 'Some posts failed to publish. Review the toast errors and try again.'
              : 'Some posts failed to schedule. Review the toast errors and try again.',
            severity: 'warning',
          });
        }

        // Cleanup progress tracking for immediate posts
        if (isImmediatePost) {
          setTimeout(() => {
            setPostingProgress(false);
            setPlatformStatus({});
            setSelectedPostForMenu(null);
          }, 2000);
        }
      } else {
        // Schedule an existing post
        const post = posts.find((p) => p.id === id);
        if (!post) {
          throw new Error('Post not found');
        }

        try {
          postsDebug('Scheduling post with ID:', id, 'at time:', estISOString);
          const response = await schedulingAPI.reschedulePost(id, estISOString);
          postsDebug('Post rescheduled successfully:', response);

          setSnackbar({
            open: true,
            message: 'Post scheduled successfully!',
            severity: 'success',
          });

          // Close the dialog first
          setSchedulingId(null);

          // Close the edit form as well
          handleCloseForm();

          // Then refresh the posts
          await fetchPosts();
          postsDebug('Posts refreshed after scheduling');
        } catch (error) {
          console.error('Error scheduling post:', error);
          showError(formatApiError(error, 'Failed to schedule post'));
          setSnackbar({
            open: true,
            message: 'Failed to schedule post. Please try again.',
            severity: 'error',
          });
        }
      }
    } catch (error) {
      console.error('Error in handleSchedule:', error);
      showError(formatApiError(error, 'Error scheduling posts'));
      setSnackbar({
        open: true,
        message: 'Error scheduling posts.',
        severity: 'error',
      });
    }
  };

  // Posting Progress Component

  const handlePublish = useStableCallback(async (id) => {
    try {
      postsDebug(`Publishing existing post ${id}`);

      // Get the post to determine platforms
      const post = posts.find((p) => p.id === id);
      if (!post) {
        throw new Error('Post not found');
      }

      const platforms =
        post.platforms?.length > 0
          ? post.platforms
          : post.platform
            ? [post.platform]
            : [];
      if (platforms.length === 0) {
        throw new Error('No platforms configured for this post');
      }

      // Set the selected post for progress tracking
      setSelectedPostForMenu(post);

      // Initialize progress tracking
      setPostingProgress(true);
      const initialStatus = {};
      platforms.forEach((platform) => {
        initialStatus[platform] = 'pending';
      });
      setPlatformStatus(initialStatus);

      // Simulate progress updates (in real implementation, this would come from WebSocket/SSE)
      const updateProgress = async () => {
        for (let i = 0; i < platforms.length; i++) {
          const platform = platforms[i];

          // Update status to processing
          setPlatformStatus((prev) => ({ ...prev, [platform]: 'processing' }));

          try {
            // Simulate individual platform posting with delay
            await new Promise((resolve) =>
              setTimeout(resolve, 1500 + Math.random() * 1000),
            );

            // Update status to success
            setPlatformStatus((prev) => ({ ...prev, [platform]: 'success' }));
          } catch (error) {
            // Update status to error
            setPlatformStatus((prev) => ({ ...prev, [platform]: 'error' }));
          }
        }
      };

      // Start progress updates
      updateProgress();

      // Wait for all platforms to complete
      await postsAPI.publishPost(id);

      setSnackbar({
        open: true,
        message: 'Post published successfully!',
        severity: 'success',
      });
      fetchPosts();
    } catch (err) {
      console.error(`Error publishing post ${id}:`, err);
      const msg = formatApiError(err, 'Failed to publish post');
      setError(msg);
      showError(msg);
    } finally {
      // Keep progress dialog open for a moment to show completion
      setTimeout(() => {
        setPostingProgress(false);
        setPlatformStatus({});
        setSelectedPostForMenu(null);
      }, 2000);
    }
  });

  const handleShareToGroups = async (post) => {
    if (!post?.id) return;
    const isFacebook = (post.platform || '').toLowerCase() === 'facebook';
    if (!isFacebook) {
      setSnackbar({ open: true, message: 'Share to groups is only for Facebook posts.', severity: 'info' });
      return;
    }
    setShareToGroupsPostId(post.id);
    try {
      const result = await groupSharingAPI.sharePost(post.id, post.platform_account_id ?? undefined);
      const gs = result?.group_share;
      const ok = gs?.ok_count;
      const total = gs?.total ?? ((gs?.ok_count || 0) + (gs?.failed_count || 0));
      const msg =
        total > 0
          ? `Group share finished: ${ok ?? 0}/${total} succeeded. Expand Groups on the post for details.`
          : result?.success
            ? 'Share to groups completed.'
            : 'Share to groups finished with errors. Expand Groups on the post for details.';
      setSnackbar({
        open: true,
        message: msg,
        severity: result?.success ? 'success' : 'warning',
      });
      fetchPosts();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Share to groups failed.';
      setSnackbar({ open: true, message: typeof msg === 'string' ? msg : JSON.stringify(msg), severity: 'error' });
    } finally {
      setShareToGroupsPostId(null);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      client_id: '',
      platform: '',
      status: '',
    });
  };

  const getFilteredPlatformAccounts = () => {
    if (!form.client_id) return platformAccounts;
    return platformAccounts.filter(
      (account) => account.client_id === form.client_id,
    );
  };

  const appendGroupShareText = (payload, platformsList) => {
    const wantsFb = (platformsList || []).some(
      (p) => String(p || '').toLowerCase() === 'facebook',
    );
    if (!wantsFb) return payload;
    const text = (form.group_share_text || '').trim();
    payload.group_share_text = text
      ? text.slice(0, GROUP_SHARE_TEXT_MAX_LENGTH)
      : null;
    // Per-group captions: send every entry so a cleared box removes its stored
    // override (blank values are dropped server-side). Only groups that exist.
    const perGroup = {};
    Object.entries(form.group_share_texts || {}).forEach(([name, val]) => {
      const t = String(val || '').trim();
      if (name && t) perGroup[name] = t.slice(0, GROUP_SHARE_TEXT_MAX_LENGTH);
    });
    payload.group_share_texts = perGroup;
    return payload;
  };

  // Names of the Page's configured share groups, for the per-group caption boxes.
  const [shareGroupNames, setShareGroupNames] = useState([]);
  const wantsFacebookShare = (form.platforms || []).some(
    (p) => String(p || '').toLowerCase() === 'facebook',
  );
  useEffect(() => {
    let cancelled = false;
    if (!wantsFacebookShare || !form.client_id) {
      setShareGroupNames([]);
      return undefined;
    }
    (async () => {
      try {
        const account = await resolvePlatformAccount(form.client_id, 'facebook');
        if (!account) return;
        const cfg = await groupSharingAPI.getConfig(account.id);
        const names = (cfg?.group_ids || [])
          .map((g) => g?.name || g?.id)
          .filter(Boolean);
        if (!cancelled) setShareGroupNames([...new Set(names)]);
      } catch (e) {
        if (!cancelled) setShareGroupNames([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsFacebookShare, form.client_id]);

  const handleClientChange = (clientId) => {
    const defaults = clientId
      ? defaultConnectedPlatforms(platformAccounts, clientId)
      : [];
    setForm((prev) => ({
      ...prev,
      client_id: clientId,
      // 16.R3: all connected platforms pre-checked (empty if none connected).
      platforms: defaults,
    }));

    if (clientId) {
      fetchYoutubePlaylists(clientId);
      setPinterestBoards([]);
    } else {
      setYoutubePlaylists([]);
      setPinterestBoards([]);
    }
  };


  const getClientFacebookInfo = (clientId) => {
    const client = clients.find((c) => c.id === parseInt(clientId));
    return client?.facebook_page_name
      ? {
          pageName: client.facebook_page_name,
          pageId: client.facebook_page_id,
        }
      : null;
  };

  const selectedClient = clients.find((c) => c.id === parseInt(form.client_id));

  // Handler for selecting/deselecting a single post
  const handleSelectPost = useCallback((id) => {
    setSelectedPosts((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id],
    );
  }, []);

  // Handler for selecting/deselecting all posts (table tab uses filtered/sorted rows)
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedPosts(posts.map((post) => post.id));
    } else {
      setSelectedPosts([]);
    }
  };

  const handleTableSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedPosts(tableViewPosts.map((post) => post.id));
    } else {
      setSelectedPosts([]);
    }
  };

  // Enhanced bulk actions
  const handleBulkAction = async (action) => {
    if (selectedPosts.length === 0) return;

    try {
      switch (action) {
        case 'delete':
          setConfirmDialog({
            open: true,
            action: 'delete',
            data: { type: 'bulk', ids: [...selectedPosts] },
          });
          return;
        case 'pause':
          await schedulingAPI.pauseSchedule(selectedPosts);
          break;
        case 'reschedule':
          setBulkActionDialog({ open: true, action: 'reschedule' });
          return;
        default:
          break;
      }
      setSelectedPosts([]);
      fetchPosts();
      setSnackbar({
        open: true,
        message: `Bulk ${action} completed successfully!`,
        severity: 'success',
      });
    } catch (err) {
      const msg = formatApiError(err, `Failed to perform bulk ${action}`);
      showError(msg);
      setSnackbar({
        open: true,
        message: msg,
        severity: 'error',
      });
    }
  };

  const handleBulkReschedule = async () => {
    try {
      await schedulingAPI.bulkSchedulePosts(
        selectedPosts,
        bulkScheduledTime,
        bulkTimezone,
      );
      setSelectedPosts([]);
      setBulkActionDialog({ open: false, action: null });
      fetchPosts();
      setSnackbar({
        open: true,
        message: 'Posts rescheduled successfully!',
        severity: 'success',
      });
    } catch (err) {
      const msg = formatApiError(err, 'Failed to reschedule posts');
      showError(msg);
      setSnackbar({
        open: true,
        message: msg,
        severity: 'error',
      });
    }
  };

  // Menu handlers
  const handleMenuOpen = useCallback((event, post) => {
    setAnchorEl(event.currentTarget);
    setSelectedPostForMenu(post);
  }, []);

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPostForMenu(null);
  };

  // Reschedule handlers
  const handleReschedule = useCallback((post) => {
    setRescheduleDialog({
      open: true,
      post,
      newTime: post.scheduled_time ? new Date(post.scheduled_time) : new Date(),
    });
  }, []);

  const handleRescheduleSubmit = async () => {
    try {
      await schedulingAPI.reschedulePost(
        rescheduleDialog.post.id,
        rescheduleDialog.newTime,
      );
      setRescheduleDialog({ open: false, post: null, newTime: new Date() });
      fetchPosts();
      setSnackbar({
        open: true,
        message: 'Post rescheduled successfully!',
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to reschedule post',
        severity: 'error',
      });
    }
  };

  // Preview handlers
  const handlePreview = useCallback((post) => {
    setPreviewDialog({ open: true, post });
  }, []);

  // Recurring post handlers
  const handleCreateRecurring = (post) => {
    setRecurringDialog({ open: true, post });
  };

  const handleRecurringSubmit = async () => {
    try {
      const scheduleConfig = {
        ...recurringConfig,
        timezone: bulkTimezone,
      };
      await schedulingAPI.createRecurringSchedule(
        recurringDialog.post,
        scheduleConfig,
      );
      setRecurringDialog({ open: false, post: null });
      fetchPosts();
      setSnackbar({
        open: true,
        message: 'Recurring schedule created successfully!',
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to create recurring schedule',
        severity: 'error',
      });
    }
  };

  // Helper function to check if post is recurring
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <PageErrorAlert message={error} onClose={() => setError(null)} />
        {/* Dashboard Stats */}
        <Typography variant="h4" gutterBottom>
          Posts
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create, edit, publish, and review posts. Calendar, bulk schedule,
          recurring, and upcoming views live in{' '}
          <Link component={RouterLink} to="/scheduling" underline="hover">
            Scheduling
          </Link>
          .
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Button
            component={RouterLink}
            to="/scheduling"
            variant="outlined"
            startIcon={<CalendarTodayIcon />}
          >
            Open Scheduling
          </Button>
        </Box>

        {loading ? (
          <PageLoadingState minHeight={400} message="Loading posts…" />
        ) : (
          <>
        <PostsStatsCards stats={stats} />

        {/* Tab Navigation */}
        <Paper sx={{ mt: 3, mb: 3 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="All posts" />
            <Tab label="Failed" />
            <Tab label="Analytics" />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        {tab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mb: 2,
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => handleBulkAction('delete')}
                  disabled={selectedPosts.length === 0}
                  startIcon={<Delete />}
                >
                  Delete Selected ({selectedPosts.length})
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => handleBulkAction('pause')}
                  disabled={selectedPosts.length === 0}
                  startIcon={<Pause />}
                >
                  Pause Selected ({selectedPosts.length})
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleBulkAction('reschedule')}
                  disabled={selectedPosts.length === 0}
                  startIcon={<Schedule />}
                >
                  Reschedule Selected ({selectedPosts.length})
                </Button>
              </Box>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleOpenForm()}
                startIcon={<Add />}
                sx={{ minWidth: 150 }}
              >
                Create New Post
              </Button>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'center',
                mb: 1,
              }}
            >
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 260 }, width: { xs: '100%', sm: 'auto' } }}>
                <InputLabel id="posts-table-client-filter-label">
                  Filter by client
                </InputLabel>
                <Select
                  labelId="posts-table-client-filter-label"
                  label="Filter by client"
                  value={tableClientFilterId}
                  onChange={(e) => setTableClientFilterId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>All clients</em>
                  </MenuItem>
                  {[...clients]
                    .sort((a, b) =>
                      (a.name || '').localeCompare(b.name || '', undefined, {
                        sensitivity: 'base',
                      }),
                    )
                    .map((c) => (
                      <MenuItem key={c.id} value={String(c.id)}>
                        {c.name || `Client ${c.id}`}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                Sort by column headers (title, client, scheduled, published). Showing{' '}
                {tableViewPosts.length} of {Array.isArray(posts) ? posts.length : 0}{' '}
                posts
                {tableClientFilterId ? ' (filtered)' : ''}.
              </Typography>
            </Box>
            <DataTable component={Paper}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          tableViewPosts.length > 0 &&
                          tableSelectedCount > 0 &&
                          tableSelectedCount < tableViewPosts.length
                        }
                        checked={
                          tableViewPosts.length > 0 &&
                          tableSelectedCount === tableViewPosts.length
                        }
                        onChange={handleTableSelectAll}
                      />
                    </TableCell>
                    <TableCell
                      sortDirection={
                        tableSort.field === 'title' ? tableSort.direction : false
                      }
                    >
                      <TableSortLabel
                        active={tableSort.field === 'title'}
                        direction={tableSort.direction}
                        onClick={() => handleTableSort('title')}
                      >
                        Title
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={
                        tableSort.field === 'client'
                          ? tableSort.direction
                          : false
                      }
                    >
                      <TableSortLabel
                        active={tableSort.field === 'client'}
                        direction={tableSort.direction}
                        onClick={() => handleTableSort('client')}
                      >
                        Client
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Platforms (target / this row)</TableCell>
                    <TableCell>Content Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell
                      sortDirection={
                        tableSort.field === 'scheduled'
                          ? tableSort.direction
                          : false
                      }
                    >
                      <TableSortLabel
                        active={tableSort.field === 'scheduled'}
                        direction={tableSort.direction}
                        onClick={() => handleTableSort('scheduled')}
                      >
                        Scheduled
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={
                        tableSort.field === 'published'
                          ? tableSort.direction
                          : false
                      }
                    >
                      <TableSortLabel
                        active={tableSort.field === 'published'}
                        direction={tableSort.direction}
                        onClick={() => handleTableSort('published')}
                      >
                        Published
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Engagement</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableViewPosts.map((post) => (
                    <PostTableRow
                      key={post.id}
                      post={post}
                      selected={selectedPostIds.has(post.id)}
                      onSelect={handleSelectPost}
                      onPreview={handlePreview}
                      onView={handleView}
                      onEdit={handleOpenForm}
                      onReschedule={handleReschedule}
                      onSchedule={setSchedulingId}
                      onPublish={handlePublish}
                      onMenuOpen={handleMenuOpen}
                    />
                  ))}
                  {tableViewPosts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          {tableClientFilterId
                            ? 'No posts for this client. Try another client or show all.'
                            : 'No posts to display.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
            </DataTable>
          </Box>
        )}
        {tab === 1 && (
          <Box>
            {/* Failed Posts Tab */}
            <Box
              sx={{
                mb: 3,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: '#f0f6fc',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #f85149, #da3633)',
                    borderRadius: 2,
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ErrorOutlineIcon sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                Failed Posts
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => window.location.reload()}
                  sx={{
                    borderColor: '#f85149',
                    color: '#f85149',
                    '&:hover': {
                      borderColor: '#da3633',
                      bgcolor: 'rgba(248, 81, 73, 0.1)',
                    },
                  }}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => handleOpenForm()}
                  sx={{
                    bgcolor: '#238636',
                    '&:hover': { bgcolor: '#2ea043' },
                  }}
                >
                  Create New Post
                </Button>
              </Box>
            </Box>

            {/* Stats Cards */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mb: 3,
                flexWrap: 'wrap',
                justifyContent: 'space-between',
              }}
            >
              <Card
                sx={{
                  flex: '1 1 200px',
                  minWidth: { xs: '100%', sm: '160px' },
                  p: 2,
                  bgcolor: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: 2,
                  background:
                    'linear-gradient(135deg, #21262d 0%, #1a1a1a 100%)',
                  '&:hover': {
                    borderColor: '#f85149',
                    boxShadow: '0 4px 20px rgba(248, 81, 73, 0.1)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      background: 'linear-gradient(135deg, #f85149, #da3633)',
                    }}
                  >
                    <ErrorOutlineIcon sx={{ color: '#ffffff', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{ color: '#f0f6fc', fontWeight: 600, lineHeight: 1 }}
                    >
                      {posts.filter((p) => p.status === 'failed').length}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#7d8590', fontSize: '0.75rem' }}
                    >
                      Failed
                    </Typography>
                  </Box>
                </Box>
              </Card>

              <Card
                sx={{
                  flex: '1 1 200px',
                  minWidth: { xs: '100%', sm: '160px' },
                  p: 2,
                  bgcolor: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: 2,
                  background:
                    'linear-gradient(135deg, #21262d 0%, #1a1a1a 100%)',
                  '&:hover': {
                    borderColor: '#f78166',
                    boxShadow: '0 4px 20px rgba(247, 129, 102, 0.1)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      background: 'linear-gradient(135deg, #f78166, #f0883e)',
                    }}
                  >
                    <Pause sx={{ color: '#ffffff', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{ color: '#f0f6fc', fontWeight: 600, lineHeight: 1 }}
                    >
                      {posts.filter((p) => p.status === 'paused').length}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#7d8590', fontSize: '0.75rem' }}
                    >
                      Paused
                    </Typography>
                  </Box>
                </Box>
              </Card>

              <Card
                sx={{
                  flex: '1 1 200px',
                  minWidth: { xs: '100%', sm: '160px' },
                  p: 2,
                  bgcolor: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: 2,
                  background:
                    'linear-gradient(135deg, #21262d 0%, #1a1a1a 100%)',
                  '&:hover': {
                    borderColor: '#58a6ff',
                    boxShadow: '0 4px 20px rgba(88, 166, 255, 0.1)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      background: 'linear-gradient(135deg, #58a6ff, #1f6feb)',
                    }}
                  >
                    <PlayArrow sx={{ color: '#ffffff', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{ color: '#f0f6fc', fontWeight: 600, lineHeight: 1 }}
                    >
                      {posts.filter((p) => p.status === 'scheduled').length}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#7d8590', fontSize: '0.75rem' }}
                    >
                      Ready to Retry
                    </Typography>
                  </Box>
                </Box>
              </Card>

              <Card
                sx={{
                  flex: '1 1 200px',
                  minWidth: { xs: '100%', sm: '160px' },
                  p: 2,
                  bgcolor: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: 2,
                  background:
                    'linear-gradient(135deg, #21262d 0%, #1a1a1a 100%)',
                  '&:hover': {
                    borderColor: '#238636',
                    boxShadow: '0 4px 20px rgba(35, 134, 54, 0.1)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      background: 'linear-gradient(135deg, #238636, #2ea043)',
                    }}
                  >
                    <Publish sx={{ color: '#ffffff', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{ color: '#f0f6fc', fontWeight: 600, lineHeight: 1 }}
                    >
                      {posts.filter((p) => p.status === 'published').length}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#7d8590', fontSize: '0.75rem' }}
                    >
                      Published
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Box>

            {/* Failed Posts List */}
            <Card sx={{ bgcolor: '#21262d', border: '1px solid #30363d' }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ p: 3, borderBottom: '1px solid #30363d' }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#f0f6fc',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <ErrorOutlineIcon sx={{ color: '#f85149' }} />
                    Failed Posts Management
                  </Typography>
                </Box>

                {(() => {
                  const failedPosts = posts.filter(
                    (p) => p.status === 'failed' || p.status === 'paused',
                  );

                  if (failedPosts.length === 0) {
                    return (
                      <Box sx={{ p: 6, textAlign: 'center' }}>
                        <Box
                          sx={{
                            background:
                              'linear-gradient(135deg, #238636, #2ea043)',
                            borderRadius: '50%',
                            p: 3,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 3,
                          }}
                        >
                          <Publish sx={{ color: 'white', fontSize: 40 }} />
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{ color: '#f0f6fc', mb: 2 }}
                        >
                          No Failed Posts
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: '#7d8590', mb: 3 }}
                        >
                          Great! All your posts are publishing successfully.
                          Keep up the good work!
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => handleOpenForm()}
                          sx={{
                            bgcolor: '#238636',
                            '&:hover': { bgcolor: '#2ea043' },
                          }}
                        >
                          Create New Post
                        </Button>
                      </Box>
                    );
                  }

                  return (
                    <Box sx={{ p: 3 }}>
                      {failedPosts.map((post, index) => (
                        <Box key={post.id} sx={{ mb: 3 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: { xs: 'column', sm: 'row' },
                              alignItems: { xs: 'stretch', sm: 'flex-start' },
                              gap: { xs: 2, sm: 3 },
                              p: { xs: 2, sm: 3 },
                              bgcolor: '#161b22',
                              borderRadius: 2,
                              border: '1px solid #30363d',
                              maxWidth: '100%',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                borderColor:
                                  post.status === 'failed'
                                    ? '#f85149'
                                    : '#f78166',
                                boxShadow: `0 4px 20px ${
                                  post.status === 'failed'
                                    ? 'rgba(248, 81, 73, 0.1)'
                                    : 'rgba(247, 129, 102, 0.1)'
                                }`,
                              },
                            }}
                          >
                            {/* Error Indicator */}
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                minWidth: 60,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  bgcolor:
                                    post.status === 'failed'
                                      ? '#f85149'
                                      : '#f78166',
                                  mb: 1,
                                }}
                              />
                              {index < failedPosts.length - 1 && (
                                <Box
                                  sx={{
                                    width: 2,
                                    height: 40,
                                    bgcolor: '#30363d',
                                    borderRadius: 1,
                                  }}
                                />
                              )}
                            </Box>

                            {/* Post Content */}
                            <Box sx={{ flex: 1 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  mb: 2,
                                }}
                              >
                                <Box>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      color: '#f0f6fc',
                                      fontWeight: 600,
                                      mb: 1,
                                    }}
                                  >
                                    {post.title || 'Untitled Post'}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: '#7d8590', mb: 2 }}
                                  >
                                    {post.content?.substring(0, 150)}...
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <Chip
                                    label={post.status}
                                    color={getStatusColor(post.status)}
                                    size="small"
                                    sx={{ fontWeight: 500 }}
                                  />
                                  {isRecurringPost(post) && (
                                    <Tooltip title="Recurring Post" arrow>
                                      <Repeat
                                        color="primary"
                                        fontSize="small"
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              </Box>

                              {/* Timeline Summary */}
                              <Box
                                sx={{
                                  p: 2,
                                  bgcolor: '#1a1a1a',
                                  border: '1px solid #30363d',
                                  borderRadius: 1,
                                  mb: 2,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#7d8590',
                                    fontWeight: 500,
                                    mb: 1,
                                  }}
                                >
                                  Timeline:
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    gap: 3,
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  {post.scheduled_time && (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                      }}
                                    >
                                      <CalendarToday
                                        sx={{ color: '#58a6ff', fontSize: 14 }}
                                      />
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: '#f0f6fc',
                                          fontSize: '0.75rem',
                                        }}
                                      >
                                        Scheduled:{' '}
                                        {formatToEST(post.scheduled_time)}
                                      </Typography>
                                    </Box>
                                  )}
                                  {post.failed_at && (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                      }}
                                    >
                                      <ErrorOutlineIcon
                                        sx={{ color: '#f85149', fontSize: 14 }}
                                      />
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: '#f85149',
                                          fontSize: '0.75rem',
                                        }}
                                      >
                                        Failed: {formatToEST(post.failed_at)}
                                      </Typography>
                                    </Box>
                                  )}
                                  {post.retry_count && post.retry_count > 0 && (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                      }}
                                    >
                                      <Autorenew
                                        sx={{ color: '#f78166', fontSize: 14 }}
                                      />
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: '#f78166',
                                          fontSize: '0.75rem',
                                        }}
                                      >
                                        Retries: {post.retry_count}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </Box>

                              {/* Error Details */}
                              {getPostPublishError(post) && (
                                <Box
                                  sx={{
                                    p: 2,
                                    bgcolor: '#2d1b1b',
                                    border: '1px solid #f85149',
                                    borderRadius: 1,
                                    mb: 2,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#f85149',
                                      fontWeight: 500,
                                      mb: 1,
                                    }}
                                  >
                                    Error details:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: '#f0f6fc' }}
                                  >
                                    {getPostPublishError(post)}
                                  </Typography>
                                </Box>
                              )}

                              {post.group_share && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#8b949e',
                                      fontWeight: 500,
                                      mb: 1,
                                    }}
                                  >
                                    Group share status
                                  </Typography>
                                  <GroupShareStatus post={post} />
                                </Box>
                              )}

                              {/* Post Details */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 4,
                                  mb: 2,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ color: '#7d8590' }}
                                  >
                                    Client:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: '#f0f6fc', fontWeight: 500 }}
                                  >
                                    {post.client?.name || 'N/A'}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ color: '#7d8590' }}
                                  >
                                    Platform:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: '#f0f6fc', fontWeight: 500 }}
                                  >
                                    {post.platform}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ color: '#7d8590' }}
                                  >
                                    Type:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: '#f0f6fc', fontWeight: 500 }}
                                  >
                                    {post.content_type}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Timeline Information */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1,
                                  mb: 2,
                                }}
                              >
                                {post.scheduled_time && (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                    }}
                                  >
                                    <CalendarToday
                                      sx={{ color: '#58a6ff', fontSize: 16 }}
                                    />
                                    <Typography
                                      variant="body2"
                                      sx={{ color: '#f0f6fc', fontWeight: 500 }}
                                    >
                                      Originally scheduled for:{' '}
                                      {formatToEST(post.scheduled_time)}
                                    </Typography>
                                  </Box>
                                )}

                                {post.failed_at && (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                    }}
                                  >
                                    <ErrorOutlineIcon
                                      sx={{ color: '#f85149', fontSize: 16 }}
                                    />
                                    <Typography
                                      variant="body2"
                                      sx={{ color: '#f85149', fontWeight: 500 }}
                                    >
                                      Failed on: {formatToEST(post.failed_at)}
                                    </Typography>
                                  </Box>
                                )}

                                {post.updated_at && (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                    }}
                                  >
                                    <AccessTime
                                      sx={{ color: '#7d8590', fontSize: 16 }}
                                    />
                                    <Typography
                                      variant="body2"
                                      sx={{ color: '#7d8590', fontWeight: 500 }}
                                    >
                                      Last updated:{' '}
                                      {formatToEST(post.updated_at)}
                                    </Typography>
                                  </Box>
                                )}

                                {post.retry_count && post.retry_count > 0 && (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                    }}
                                  >
                                    <Autorenew
                                      sx={{ color: '#f78166', fontSize: 16 }}
                                    />
                                    <Typography
                                      variant="body2"
                                      sx={{ color: '#f78166', fontWeight: 500 }}
                                    >
                                      Retry attempts: {post.retry_count}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>

                              {/* Actions */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 1,
                                  flexWrap: 'wrap',
                                  flexDirection: { xs: 'column', sm: 'row' },
                                  width: '100%',
                                }}
                              >
                                <Button
                                  size="large"
                                  startIcon={<Preview />}
                                  onClick={() => handlePreview(post)}
                                  sx={{
                                    color: '#58a6ff',
                                    minHeight: 44,
                                    width: { xs: '100%', sm: 'auto' },
                                    '&:hover': {
                                      bgcolor: 'rgba(88, 166, 255, 0.1)',
                                    },
                                  }}
                                >
                                  Preview
                                </Button>
                                <Button
                                  size="large"
                                  startIcon={<Edit />}
                                  onClick={() => handleOpenForm(post)}
                                  sx={{
                                    color: '#f78166',
                                    minHeight: 44,
                                    width: { xs: '100%', sm: 'auto' },
                                    '&:hover': {
                                      bgcolor: 'rgba(247, 129, 102, 0.1)',
                                    },
                                  }}
                                >
                                  Edit
                                </Button>
                                {post.status === 'failed' && (
                                  <>
                                    <Button
                                      size="large"
                                      startIcon={<Publish />}
                                      onClick={() => handlePublish(post.id)}
                                      sx={{
                                        color: '#238636',
                                        minHeight: 44,
                                        width: { xs: '100%', sm: 'auto' },
                                        '&:hover': {
                                          bgcolor: 'rgba(35, 134, 54, 0.1)',
                                        },
                                      }}
                                    >
                                      Retry Now
                                    </Button>
                                    <Button
                                      size="large"
                                      startIcon={<Schedule />}
                                      onClick={() => handleReschedule(post)}
                                      sx={{
                                        color: '#58a6ff',
                                        minHeight: 44,
                                        width: { xs: '100%', sm: 'auto' },
                                        '&:hover': {
                                          bgcolor: 'rgba(88, 166, 255, 0.1)',
                                        },
                                      }}
                                    >
                                      Reschedule
                                    </Button>
                                  </>
                                )}
                                {post.status === 'paused' && (
                                  <Button
                                    size="small"
                                    startIcon={<PlayArrow />}
                                    onClick={() => handlePublish(post.id)}
                                    sx={{
                                      color: '#238636',
                                      '&:hover': {
                                        bgcolor: 'rgba(35, 134, 54, 0.1)',
                                      },
                                    }}
                                  >
                                    Resume
                                  </Button>
                                )}
                                {post.status === 'published' && (post.platform || '').toLowerCase() === 'facebook' && (
                                  <Button
                                    size="small"
                                    startIcon={shareToGroupsPostId === post.id ? <CircularProgress size={16} /> : <Share />}
                                    onClick={() => handleShareToGroups(post)}
                                    disabled={!!shareToGroupsPostId}
                                    sx={{
                                      color: '#58a6ff',
                                      '&:hover': {
                                        bgcolor: 'rgba(88, 166, 255, 0.1)',
                                      },
                                    }}
                                  >
                                    Share to groups
                                  </Button>
                                )}
                                <Button
                                  size="small"
                                  startIcon={<Delete />}
                                  onClick={() => handleDelete(post.id)}
                                  sx={{
                                    color: '#f85149',
                                    '&:hover': {
                                      bgcolor: 'rgba(248, 81, 73, 0.1)',
                                    },
                                  }}
                                >
                                  Delete
                                </Button>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
              </CardContent>
            </Card>
          </Box>
        )}
        {tab === 2 && (
          <PostsAnalyticsTab
            posts={posts}
            onRefresh={() => fetchPosts({ userInitiated: true })}
          />
        )}

          </>
        )}

        {/* Create/Edit Post Dialog */}
        <Dialog
          open={openForm}
          onClose={handleCloseForm}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgba(13, 17, 23, 0.98)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(48, 54, 61, 0.6)',
              borderRadius: 4,
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4)',
              maxHeight: '95vh',
              overflow: 'hidden',
            },
          }}
        >
          <DialogTitle
            sx={{
              color: '#f0f6fc',
              borderBottom: '1px solid rgba(48, 54, 61, 0.4)',
              pb: 3,
              pt: 3,
              px: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              background: 'rgba(13, 17, 23, 0.8)',
            }}
          >
            <Box
              sx={{
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                borderRadius: 3,
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
              }}
            >
              <Add sx={{ color: 'white', fontSize: 22 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '20px' }}>
              {editingId ? 'Edit Post' : 'Create Post'}
            </Typography>
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent
              sx={{ p: 5, maxHeight: '75vh', overflow: 'auto', pb: 8 }}
            >
              {/* Basic Information Section */}
              <Box
                sx={{
                  mb: 5,
                  background: 'rgba(22, 27, 34, 0.3)',
                  borderRadius: 4,
                  p: 4,
                  border: '1px solid rgba(48, 54, 61, 0.2)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    borderColor: 'rgba(48, 54, 61, 0.4)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    color: '#f0f6fc',
                    fontWeight: 700,
                    fontSize: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #667eea, #764ba2)',
                      borderRadius: 3,
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                    }}
                  >
                    <Add sx={{ color: 'white', fontSize: 18 }} />
                  </Box>
                  Basic Information
                </Typography>

                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                  <Box sx={{ flex: '0 0 75%', position: 'relative' }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1.5,
                        color: '#f0f6fc',
                        fontWeight: 600,
                        fontSize: '14px',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Post Title
                    </Typography>
                    <TextField
                      name="title"
                      value={form.title}
                      onChange={handleChange}
                      fullWidth
                      placeholder="Enter a compelling title for your post"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'rgba(13, 17, 23, 0.95)',
                          borderRadius: 3,
                          transition: 'all 0.2s ease-in-out',
                          height: '56px',
                          '& fieldset': {
                            borderColor: 'rgba(48, 54, 61, 0.4)',
                            borderWidth: '1px',
                            transition: 'all 0.2s ease-in-out',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(88, 166, 255, 0.6)',
                            borderWidth: '1px',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#58a6ff',
                            borderWidth: '2px',
                            boxShadow: '0 0 0 4px rgba(88, 166, 255, 0.1)',
                          },
                        },
                      }}
                      InputProps={{
                        sx: {
                          color: '#f0f6fc',
                          py: 2.5,
                          px: 3,
                          fontSize: '16px',
                          fontWeight: 500,
                          height: '56px',
                          '&::placeholder': {
                            color: '#6e7681',
                            opacity: 1,
                          },
                        },
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: '0 0 25%', position: 'relative' }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1.5,
                        color: '#f0f6fc',
                        fontWeight: 600,
                        fontSize: '14px',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Content Type
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        name="content_type"
                        value={form.content_type}
                        onChange={handleChange}
                        displayEmpty
                        sx={{
                          color: '#f0f6fc',
                          py: 2.5,
                          px: 3,
                          fontSize: '16px',
                          fontWeight: 500,
                          bgcolor: 'rgba(13, 17, 23, 0.95)',
                          borderRadius: 3,
                          transition: 'all 0.2s ease-in-out',
                          height: '56px',
                          '& .MuiSelect-select': {
                            py: 0,
                            height: '56px',
                            display: 'flex',
                            alignItems: 'center',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(48, 54, 61, 0.4)',
                            borderWidth: '1px',
                            transition: 'all 0.2s ease-in-out',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(88, 166, 255, 0.6)',
                            borderWidth: '1px',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#58a6ff',
                            borderWidth: '2px',
                            boxShadow: '0 0 0 4px rgba(88, 166, 255, 0.1)',
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              bgcolor: 'rgba(13, 17, 23, 0.98)',
                              border: '1px solid rgba(48, 54, 61, 0.6)',
                              borderRadius: 3,
                              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
                              mt: 1,
                              '& .MuiMenuItem-root': {
                                color: '#f0f6fc',
                                fontSize: '16px',
                                py: 2,
                                px: 3,
                                borderRadius: 2,
                                mx: 1,
                                mb: 0.5,
                                '&:hover': {
                                  bgcolor: 'rgba(88, 166, 255, 0.15)',
                                },
                                '&.Mui-selected': {
                                  bgcolor: 'rgba(88, 166, 255, 0.25)',
                                  '&:hover': {
                                    bgcolor: 'rgba(88, 166, 255, 0.35)',
                                  },
                                },
                              },
                            },
                          },
                        }}
                      >
                        {contentTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box sx={{ flex: '0 0 25%', position: 'relative' }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1.5,
                        color: '#f0f6fc',
                        fontWeight: 600,
                        fontSize: '14px',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Theme (A/B/C)
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        name="theme"
                        value={form.theme || ''}
                        onChange={handleChange}
                        displayEmpty
                        sx={{
                          color: '#f0f6fc',
                          bgcolor: 'rgba(13, 17, 23, 0.95)',
                          borderRadius: 3,
                          height: '56px',
                          '& .MuiSelect-select': {
                            py: 0,
                            height: '56px',
                            display: 'flex',
                            alignItems: 'center',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(48, 54, 61, 0.4)',
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em>Auto / unset</em>
                        </MenuItem>
                        {themeOptions.map((t) => (
                          <MenuItem key={t} value={t}>
                            {t.charAt(0).toUpperCase() + t.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormHelperText sx={{ color: '#8b949e', mx: 0 }}>
                      Optional. Auto-detects light/dark/black from media path.
                    </FormHelperText>
                  </Box>
                </Box>
              </Box>

              {/* Content Section */}
              <Box
                sx={{
                  mb: 5,
                  background: 'rgba(22, 27, 34, 0.3)',
                  borderRadius: 4,
                  p: 4,
                  border: '1px solid rgba(48, 54, 61, 0.2)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    borderColor: 'rgba(48, 54, 61, 0.4)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    color: '#f0f6fc',
                    fontWeight: 700,
                    fontSize: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #28a745, #20c997)',
                      borderRadius: 3,
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)',
                    }}
                  >
                    <Publish sx={{ color: 'white', fontSize: 18 }} />
                  </Box>
                  Post Content
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    flexWrap: { xs: 'nowrap', md: 'wrap' },
                    width: '100%',
                    gap: 3,
                    boxSizing: 'border-box',
                    alignItems: 'stretch',
                  }}
                >
                  <Box
                    sx={{
                      flex: {
                        xs: '1 1 auto',
                        md: '1 1 calc(50% - 12px)',
                      },
                      minWidth: 0,
                      width: { xs: '100%', md: 'auto' },
                      maxWidth: { xs: '100%', md: 'calc(50% - 12px)' },
                      boxSizing: 'border-box',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1.5,
                        color: '#f0f6fc',
                        fontWeight: 600,
                        fontSize: '14px',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Write your post content here...
                    </Typography>
                    <PostCaptionField
                      key={captionFieldKey}
                      defaultValue={form.content || ''}
                      onDebouncedChange={commitCaption}
                      onBlurCommit={commitCaption}
                      onLiveChange={syncCaptionLive}
                      fullWidth
                      multiline
                      rows={6}
                      required
                      placeholder="Share your message, story, or announcement with your audience..."
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'rgba(13, 17, 23, 0.95)',
                          borderRadius: 3,
                          transition: 'all 0.2s ease-in-out',
                          '& fieldset': {
                            borderColor: 'rgba(48, 54, 61, 0.4)',
                            borderWidth: '1px',
                            transition: 'all 0.2s ease-in-out',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(88, 166, 255, 0.6)',
                            borderWidth: '1px',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#58a6ff',
                            borderWidth: '2px',
                            boxShadow: '0 0 0 4px rgba(88, 166, 255, 0.1)',
                          },
                        },
                      }}
                      InputProps={{
                        sx: {
                          color: '#f0f6fc',
                          py: 3,
                          px: 3,
                          fontSize: '16px',
                          lineHeight: 1.7,
                          fontWeight: 400,
                          '&::placeholder': {
                            color: '#6e7681',
                            opacity: 1,
                          },
                        },
                      }}
                    />
                    {(form.platforms || []).some(
                      (p) => String(p || '').toLowerCase() === 'facebook',
                    ) && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 1,
                            color: '#f0f6fc',
                            fontWeight: 600,
                            fontSize: '14px',
                          }}
                        >
                          Group share text (optional)
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#7d8590',
                            display: 'block',
                            mb: 1,
                            lineHeight: 1.5,
                          }}
                        >
                          Custom message when sharing this post to Facebook
                          groups. Leave blank to use the Page&apos;s Default
                          share text from Group Sharing settings (or share with
                          an empty message if that is also blank).
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          value={form.group_share_text || ''}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              group_share_text: e.target.value.slice(
                                0,
                                GROUP_SHARE_TEXT_MAX_LENGTH,
                              ),
                            }))
                          }
                          placeholder="Say something about this post in groups…"
                          variant="outlined"
                          helperText={`${(form.group_share_text || '').length.toLocaleString()} / ${GROUP_SHARE_TEXT_MAX_LENGTH.toLocaleString()}`}
                          FormHelperTextProps={{
                            sx: { color: '#7d8590' },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'rgba(13, 17, 23, 0.95)',
                              borderRadius: 3,
                              '& fieldset': {
                                borderColor: 'rgba(48, 54, 61, 0.4)',
                              },
                              '&:hover fieldset': {
                                borderColor: 'rgba(88, 166, 255, 0.6)',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#58a6ff',
                              },
                            },
                            '& .MuiInputBase-input': {
                              color: '#f0f6fc',
                            },
                          }}
                        />
                        {shareGroupNames.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography
                              variant="caption"
                              sx={{ color: '#7d8590', display: 'block', mb: 1 }}
                            >
                              Per-group caption (optional). A blank box uses the
                              text above.
                            </Typography>
                            {shareGroupNames.map((name) => (
                              <TextField
                                key={name}
                                fullWidth
                                multiline
                                minRows={2}
                                label={name}
                                value={(form.group_share_texts || {})[name] || ''}
                                onChange={(e) =>
                                  setForm((prev) => ({
                                    ...prev,
                                    group_share_texts: {
                                      ...(prev.group_share_texts || {}),
                                      [name]: e.target.value.slice(
                                        0,
                                        GROUP_SHARE_TEXT_MAX_LENGTH,
                                      ),
                                    },
                                  }))
                                }
                                sx={{
                                  mb: 1.5,
                                  '& .MuiInputBase-input': { color: '#f0f6fc' },
                                  '& .MuiInputLabel-root': { color: '#7d8590' },
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                  <Box
                    sx={{
                      flex: {
                        xs: '1 1 auto',
                        md: '1 1 calc(50% - 12px)',
                      },
                      minWidth: 0,
                      width: { xs: '100%', md: 'auto' },
                      maxWidth: { xs: '100%', md: 'calc(50% - 12px)' },
                      boxSizing: 'border-box',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <EngagementPredictionPanel
                      content={form.content}
                      platform={form.platforms}
                      clientId={form.client_id}
                      scheduledTimeISO={scheduledTimeISO}
                      contentType={form.content_type}
                    />
                    <PrePublishScorePanel
                      content={form.content}
                      platform={form.platforms}
                      clientId={form.client_id}
                      scheduledTimeISO={scheduledTimeISO}
                      contentType={form.content_type}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Distribution Settings Section */}
              <Box
                sx={{
                  mb: 5,
                  background: 'rgba(22, 27, 34, 0.3)',
                  borderRadius: 4,
                  p: 4,
                  border: '1px solid rgba(48, 54, 61, 0.2)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    borderColor: 'rgba(48, 54, 61, 0.4)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    color: '#f0f6fc',
                    fontWeight: 700,
                    fontSize: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #ff6b6b, #ee5a24)',
                      borderRadius: 3,
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(255, 107, 107, 0.3)',
                    }}
                  >
                    <Share sx={{ color: 'white', fontSize: 18 }} />
                  </Box>
                  Distribution Settings
                </Typography>

                <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center' }}>
                  <Box sx={{ flex: '0 0 45%', position: 'relative' }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1.5,
                        color: '#f0f6fc',
                        fontWeight: 600,
                        fontSize: '14px',
                        letterSpacing: '0.5px',
                        textAlign: 'center',
                      }}
                    >
                      Social Media Platforms
                    </Typography>
                    <FormControl fullWidth error={!!formErrors.platforms}>
                      <Select
                        multiple
                        value={form.platforms || []}
                        onChange={handlePlatformChange}
                        displayEmpty
                        sx={{
                          color: '#f0f6fc',
                          py: 2.5,
                          px: 3,
                          fontSize: '16px',
                          fontWeight: 500,
                          bgcolor: 'rgba(13, 17, 23, 0.95)',
                          borderRadius: 3,
                          transition: 'all 0.2s ease-in-out',
                          '& .MuiSelect-select': {
                            py: 0,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(48, 54, 61, 0.4)',
                            borderWidth: '1px',
                            transition: 'all 0.2s ease-in-out',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(88, 166, 255, 0.6)',
                            borderWidth: '1px',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#58a6ff',
                            borderWidth: '2px',
                            boxShadow: '0 0 0 4px rgba(88, 166, 255, 0.1)',
                          },
                        }}
                        renderValue={(selected) =>
                          selected.length > 0
                            ? selected
                                .map(
                                  (p) =>
                                    formPlatformOptions.find(
                                      (opt) => opt.value === p?.trim(),
                                    )?.label ||
                                    PLATFORM_OPTIONS.find(
                                      (opt) => opt.value === p?.trim(),
                                    )?.label,
                                )
                                .filter(Boolean)
                                .join(', ')
                            : form.client_id
                              ? formPlatformOptions.length
                                ? 'Select platforms'
                                : 'No connected platforms — connect on Clients'
                              : 'Select a client first'
                        }
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              bgcolor: 'rgba(13, 17, 23, 0.98)',
                              border: '1px solid rgba(48, 54, 61, 0.6)',
                              borderRadius: 3,
                              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
                              mt: 1,
                              '& .MuiMenuItem-root': {
                                color: '#f0f6fc',
                                fontSize: '16px',
                                py: 2,
                                px: 3,
                                borderRadius: 2,
                                mx: 1,
                                mb: 0.5,
                                '&:hover': {
                                  bgcolor: 'rgba(88, 166, 255, 0.15)',
                                },
                                '&.Mui-selected': {
                                  bgcolor: 'rgba(88, 166, 255, 0.25)',
                                  '&:hover': {
                                    bgcolor: 'rgba(88, 166, 255, 0.35)',
                                  },
                                },
                              },
                            },
                          },
                        }}
                      >
                        {formPlatformOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            <Checkbox
                              checked={
                                (form.platforms || []).indexOf(option.value) >
                                -1
                              }
                              sx={{
                                color: '#9ca3af',
                                '&.Mui-checked': {
                                  color: '#58a6ff',
                                },
                              }}
                            />
                            <ListItemText
                              primary={option.label}
                              sx={{ color: '#f0f6fc' }}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                      {formErrors.platforms && (
                        <FormHelperText
                          sx={{ color: '#f85149', mt: 1, fontSize: '13px' }}
                        >
                          {formErrors.platforms}
                        </FormHelperText>
                      )}
                      {!formErrors.platforms && form.client_id && (
                        <FormHelperText
                          sx={{ color: '#9ca3af', mt: 1, fontSize: '12px' }}
                        >
                          Only platforms connected for this client. Instagram
                          publishes via Facebook → Instagram link.
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Box>

                  <Box sx={{ flex: '0 0 45%', position: 'relative' }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1.5,
                        color: '#f0f6fc',
                        fontWeight: 600,
                        fontSize: '14px',
                        letterSpacing: '0.5px',
                        textAlign: 'center',
                      }}
                    >
                      Client Account
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        name="client_id"
                        value={form.client_id}
                        onChange={(e) => handleClientChange(e.target.value)}
                        displayEmpty
                        sx={{
                          color: '#f0f6fc',
                          py: 2.5,
                          px: 3,
                          fontSize: '16px',
                          fontWeight: 500,
                          bgcolor: 'rgba(13, 17, 23, 0.95)',
                          borderRadius: 3,
                          transition: 'all 0.2s ease-in-out',
                          '& .MuiSelect-select': {
                            py: 0,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(48, 54, 61, 0.4)',
                            borderWidth: '1px',
                            transition: 'all 0.2s ease-in-out',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(88, 166, 255, 0.6)',
                            borderWidth: '1px',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#58a6ff',
                            borderWidth: '2px',
                            boxShadow: '0 0 0 4px rgba(88, 166, 255, 0.1)',
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              bgcolor: 'rgba(13, 17, 23, 0.98)',
                              border: '1px solid rgba(48, 54, 61, 0.6)',
                              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
                              borderRadius: 3,
                              mt: 1,
                              '& .MuiMenuItem-root': {
                                color: '#f0f6fc',
                                fontSize: '16px',
                                py: 2,
                                px: 3,
                                borderRadius: 2,
                                mx: 1,
                                mb: 0.5,
                                '&:hover': {
                                  bgcolor: 'rgba(88, 166, 255, 0.15)',
                                },
                                '&.Mui-selected': {
                                  bgcolor: 'rgba(88, 166, 255, 0.25)',
                                  '&:hover': {
                                    bgcolor: 'rgba(88, 166, 255, 0.35)',
                                  },
                                },
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em style={{ color: '#6e7681', fontStyle: 'italic' }}>
                            Select a client account
                          </em>
                        </MenuItem>
                        {clients.map((client) => (
                          <MenuItem key={client.id} value={client.id}>
                            <Box>
                              <Typography
                                sx={{
                                  color: '#f0f6fc',
                                  fontWeight: 600,
                                  fontSize: '16px',
                                }}
                              >
                                {client.name}
                              </Typography>
                              {client.facebook_page_name && (
                                <Typography
                                  sx={{
                                    color: '#9ca3af',
                                    fontSize: '14px',
                                    mt: 0.5,
                                  }}
                                >
                                  {client.facebook_page_name}
                                </Typography>
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                {/* Pinterest Settings - visible as soon as Pinterest is selected */}
                {(form.platforms || []).includes('pinterest') && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      background: 'rgba(230, 0, 35, 0.08)',
                      border: '1px solid rgba(230, 0, 35, 0.3)',
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: '#E60023',
                        fontWeight: 600,
                        mb: 2,
                        fontSize: '13px',
                      }}
                    >
                      Pinterest – Link & Board
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <TextField
                        name="pinterestLink"
                        label="Link (optional)"
                        value={form.pinterestLink || ''}
                        onChange={handleChange}
                        size="small"
                        placeholder="https://..."
                        sx={{
                          flex: '1 1 200px',
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'rgba(13, 17, 23, 0.8)',
                            '& fieldset': { borderColor: 'rgba(48, 54, 61, 0.3)' },
                          },
                        }}
                        InputLabelProps={{ sx: { color: '#8b949e' } }}
                      />
                      <TextField
                        name="pinterestBoard"
                        label="Board name"
                        value={form.pinterestBoard || ''}
                        onChange={handleChange}
                        size="small"
                        placeholder="e.g. Anime, My Board"
                        helperText="Enter exact board name (chosen during post after image upload)"
                        sx={{
                          flex: '1 1 200px',
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'rgba(13, 17, 23, 0.8)',
                            '& fieldset': { borderColor: 'rgba(48, 54, 61, 0.3)' },
                          },
                        }}
                        InputLabelProps={{ sx: { color: '#8b949e' } }}
                        FormHelperTextProps={{ sx: { color: '#8b949e', fontSize: '11px' } }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Optimization Section */}
              <Box
                sx={{
                  mb: 5,
                  background: 'rgba(22, 27, 34, 0.3)',
                  borderRadius: 4,
                  p: 4,
                  border: '1px solid rgba(48, 54, 61, 0.2)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    borderColor: 'rgba(48, 54, 61, 0.4)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    color: '#f0f6fc',
                    fontWeight: 700,
                    fontSize: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #ffd700, #ffa500)',
                      borderRadius: 3,
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
                    }}
                  >
                    <TrendingUpIcon sx={{ color: 'white', fontSize: 18 }} />
                  </Box>
                  Content Optimization
                </Typography>

                <Box sx={{ position: 'relative' }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1.5,
                      color: '#f0f6fc',
                      fontWeight: 600,
                      fontSize: '14px',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Hashtags & Keywords
                  </Typography>
                  <TextField
                    name="hashtags"
                    value={form.hashtags || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setForm({
                        ...form,
                        hashtags: value,
                      });
                    }}
                    fullWidth
                    placeholder="Enter hashtags separated by commas (e.g., #socialmedia, #marketing, #business)"
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'rgba(13, 17, 23, 0.95)',
                        borderRadius: 3,
                        transition: 'all 0.2s ease-in-out',
                        '& fieldset': {
                          borderColor: 'rgba(48, 54, 61, 0.4)',
                          borderWidth: '1px',
                          transition: 'all 0.2s ease-in-out',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(88, 166, 255, 0.6)',
                          borderWidth: '1px',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#58a6ff',
                          borderWidth: '2px',
                          boxShadow: '0 0 0 4px rgba(88, 166, 255, 0.1)',
                        },
                      },
                    }}
                    InputProps={{
                      sx: {
                        color: '#f0f6fc',
                        py: 2.5,
                        px: 3,
                        fontSize: '16px',
                        fontWeight: 400,
                        '&::placeholder': {
                          color: '#6e7681',
                          opacity: 1,
                        },
                      },
                    }}
                    FormHelperTextProps={{
                      sx: {
                        color: '#9ca3af',
                        mt: 1,
                        fontSize: '13px',
                      },
                    }}
                    helperText="Example: #socialmedia, #marketing, #business, #growth"
                  />
                </Box>
              </Box>

              {/* Media Upload Section */}
              <Box
                sx={{
                  mb: 5,
                  background: 'rgba(22, 27, 34, 0.3)',
                  borderRadius: 4,
                  p: 4,
                  border: '1px solid rgba(48, 54, 61, 0.2)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    borderColor: 'rgba(48, 54, 61, 0.4)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    color: '#f0f6fc',
                    fontWeight: 700,
                    fontSize: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #667eea, #764ba2)',
                      borderRadius: 3,
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                    }}
                  >
                    <Add sx={{ color: 'white', fontSize: 18 }} />
                  </Box>
                  Media & Assets
                </Typography>

                <Box
                  sx={{
                    background: 'rgba(13, 17, 23, 0.4)',
                    borderRadius: 3,
                    p: 4,
                    border: '1px solid rgba(48, 54, 61, 0.3)',
                  }}
                >
                  {/* File Upload */}
                  <Box sx={{ position: 'relative' }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1.5,
                        color: '#f0f6fc',
                        fontWeight: 600,
                        fontSize: '14px',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Upload Files
                    </Typography>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel id="posts-media-pillar" sx={{ color: '#8b949e' }}>
                        Content pillar
                      </InputLabel>
                      <Select
                        labelId="posts-media-pillar"
                        label="Content pillar"
                        value={mediaUploadContentPillar}
                        onChange={(e) =>
                          setMediaUploadContentPillar(e.target.value)
                        }
                        sx={{
                          color: '#f0f6fc',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(48, 54, 61, 0.6)',
                          },
                        }}
                      >
                        {MEDIA_CONTENT_PILLAR_UPLOAD_OPTIONS.map((id) => (
                          <MenuItem key={id} value={id}>
                            {MEDIA_CONTENT_PILLAR_LABELS[id] || id}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      sx={{
                        mb: 2,
                        alignItems: 'flex-start',
                        '& .MuiFormControlLabel-label': {
                          color: '#9ca3af',
                          fontSize: 14,
                          lineHeight: 1.4,
                        },
                      }}
                      control={
                        <Checkbox
                          size="small"
                          checked={mediaUploadRightsConfirmed}
                          onChange={(e) =>
                            setMediaUploadRightsConfirmed(e.target.checked)
                          }
                          disabled={postFormMediaLibraryClientId == null}
                          sx={{ color: '#8b949e', py: 0.5 }}
                        />
                      }
                      label="I have the rights to use this asset."
                    />
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{
                        py: 3,
                        border: '2px dashed',
                        borderColor: '#58a6ff',
                        color: '#58a6ff',
                        borderRadius: 3,
                        fontSize: '16px',
                        fontWeight: 500,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          borderColor: '#1f6feb',
                          bgcolor: 'rgba(88, 166, 255, 0.1)',
                          transform: 'translateY(-1px)',
                        },
                      }}
                      disabled={
                        mediaUploading ||
                        (postFormMediaLibraryClientId != null &&
                          !mediaUploadRightsConfirmed)
                      }
                      title={
                        postFormMediaLibraryClientId != null &&
                        !mediaUploadRightsConfirmed
                          ? 'Confirm usage rights to upload to this client’s media library'
                          : ''
                      }
                    >
                      {mediaUploading ? (
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <CircularProgress size={20} />
                          Uploading...
                        </Box>
                      ) : (
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Add />
                          Upload Images/Videos
                        </Box>
                      )}
                      <input
                        type="file"
                        accept="image/*,video/*"
                        multiple
                        hidden
                        onChange={handleMediaUpload}
                      />
                    </Button>

                    <Typography
                      variant="caption"
                      sx={{
                        mt: 2,
                        display: 'block',
                        color: '#9ca3af',
                        fontSize: '13px',
                        textAlign: 'center',
                      }}
                    >
                      Supported formats: JPG, PNG, GIF, MP4, MOV. Max 10 files.
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 1,
                        display: 'block',
                        color: '#6e7681',
                        fontSize: '12px',
                        textAlign: 'center',
                      }}
                    >
                      Select a <strong>client</strong> above so uploads are stored in that
                      client&apos;s gallery (Clients → Gallery).
                    </Typography>
                  </Box>

                  {/* Media URLs */}
                  <Box sx={{ mt: 4 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1.5,
                        color: '#f0f6fc',
                        fontWeight: 600,
                        fontSize: '14px',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Or Enter Media URLs
                    </Typography>
                    <TextField
                      name="media_urls"
                      value={form.media_urls || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setForm({
                          ...form,
                          media_urls: value,
                        });
                      }}
                      fullWidth
                      placeholder="https://example.com/image1.jpg, https://example.com/video1.mp4"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'rgba(13, 17, 23, 0.95)',
                          borderRadius: 3,
                          transition: 'all 0.2s ease-in-out',
                          '& fieldset': {
                            borderColor: 'rgba(48, 54, 61, 0.4)',
                            borderWidth: '1px',
                            transition: 'all 0.2s ease-in-out',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(88, 166, 255, 0.6)',
                            borderWidth: '1px',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#58a6ff',
                            borderWidth: '2px',
                            boxShadow: '0 0 0 4px rgba(88, 166, 255, 0.1)',
                          },
                        },
                      }}
                      InputProps={{
                        sx: {
                          color: '#f0f6fc',
                          py: 2.5,
                          px: 3,
                          fontSize: '16px',
                          '&::placeholder': {
                            color: '#6e7681',
                            opacity: 1,
                          },
                        },
                      }}
                      FormHelperTextProps={{
                        sx: {
                          color: '#9ca3af',
                          mt: 1,
                          fontSize: '13px',
                        },
                      }}
                      helperText="Alternative to file upload - enter URLs separated by commas"
                    />
                  </Box>

                  {/* Media Preview */}
                  {form.media_urls &&
                    (typeof form.media_urls === 'string'
                      ? form.media_urls.trim()
                      : form.media_urls.length > 0) && (
                      <Box sx={{ mt: 3 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 2,
                            color: '#f0f6fc',
                            fontWeight: 600,
                            fontSize: '14px',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Media Preview
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 2,
                            flexWrap: 'wrap',
                            p: 2,
                            bgcolor: 'rgba(13, 17, 23, 0.4)',
                            borderRadius: 2,
                            border: '1px solid rgba(48, 54, 61, 0.6)',
                          }}
                        >
                          {(typeof form.media_urls === 'string'
                            ? form.media_urls.split(',')
                            : form.media_urls || []
                          ).map((url, idx) => {
                            const trimmedUrl =
                              typeof url === 'string' ? url.trim() : url;
                            if (!trimmedUrl) return null;
                            const previewSrc =
                              trimmedUrl.startsWith('http://') ||
                              trimmedUrl.startsWith('https://')
                                ? trimmedUrl
                                : publicFileUrl(trimmedUrl);
                            return (
                              <Box
                                key={idx}
                                sx={{
                                  position: 'relative',
                                  borderRadius: 2,
                                  overflow: 'hidden',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                  transition: 'transform 0.2s ease-in-out',
                                  '&:hover': {
                                    transform: 'scale(1.05)',
                                  },
                                }}
                              >
                                {trimmedUrl.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                                  <img
                                    src={previewSrc}
                                    alt="media"
                                    style={{
                                      width: 120,
                                      height: 120,
                                      objectFit: 'cover',
                                      display: 'block',
                                    }}
                                  />
                                ) : trimmedUrl.match(/\.(mp4|mov|avi)$/i) ? (
                                  <Box
                                    sx={{
                                      width: 120,
                                      height: 120,
                                      backgroundColor: 'rgba(13, 17, 23, 0.8)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <PlayArrow
                                      sx={{ color: '#58a6ff', fontSize: 40 }}
                                    />
                                  </Box>
                                ) : (
                                  <Box
                                    sx={{
                                      width: 120,
                                      height: 120,
                                      backgroundColor: 'rgba(13, 17, 23, 0.8)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: '#9ca3af',
                                        fontSize: '12px',
                                      }}
                                    >
                                      File
                                    </Typography>
                                  </Box>
                                )}
                                <IconButton
                                  size="small"
                                  sx={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    backgroundColor: 'rgba(220, 53, 69, 0.9)',
                                    color: 'white',
                                    width: 24,
                                    height: 24,
                                    '&:hover': {
                                      backgroundColor: '#c82333',
                                      transform: 'scale(1.1)',
                                    },
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                                    transition: 'all 0.2s ease-in-out',
                                  }}
                                  onClick={() => {
                                    const urls = parseMediaUrlsField(
                                      form.media_urls,
                                    );
                                    const newUrls = urls.filter(
                                      (_, i) => i !== idx,
                                    );
                                    setForm({
                                      ...form,
                                      media_urls: newUrls.join(', '),
                                    });
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                </Box>
              </Box>

              {/* Platform Connection Status */}
              <Box
                sx={{
                  mb: 5,
                  background: 'rgba(22, 27, 34, 0.3)',
                  borderRadius: 4,
                  p: 4,
                  border: '1px solid rgba(48, 54, 61, 0.2)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    borderColor: 'rgba(48, 54, 61, 0.4)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    color: '#f0f6fc',
                    fontWeight: 700,
                    fontSize: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #17a2b8, #20c997)',
                      borderRadius: 3,
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(23, 162, 184, 0.3)',
                    }}
                  >
                    <BarChart sx={{ color: 'white', fontSize: 18 }} />
                  </Box>
                  Connection Status
                </Typography>

                <Grid container spacing={3}>
                  {SHOW_INSTAGRAM_AS_PUBLISH_TARGET &&
                    (form.platforms || []).includes('Instagram') && (
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          background: selectedClient?.instagram_connected
                            ? 'rgba(40, 167, 69, 0.15)'
                            : 'rgba(255, 193, 7, 0.15)',
                          border: '2px solid',
                          borderColor: selectedClient?.instagram_connected
                            ? '#28a745'
                            : '#ffc107',
                          borderRadius: 3,
                          p: 3,
                          textAlign: 'center',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
                          },
                        }}
                      >
                        <Instagram
                          sx={{
                            fontSize: 28,
                            color: selectedClient?.instagram_connected
                              ? '#28a745'
                              : '#ffc107',
                            mb: 1.5,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: selectedClient?.instagram_connected
                              ? '#28a745'
                              : '#ffc107',
                            fontWeight: 600,
                            fontSize: '14px',
                          }}
                        >
                          {selectedClient?.instagram_connected
                            ? 'Instagram Connected'
                            : 'Instagram Not Connected'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {(form.platforms || []).includes('LinkedIn') && (
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          background: selectedClient?.linkedin_connected
                            ? 'rgba(40, 167, 69, 0.15)'
                            : 'rgba(255, 193, 7, 0.15)',
                          border: '2px solid',
                          borderColor: selectedClient?.linkedin_connected
                            ? '#28a745'
                            : '#ffc107',
                          borderRadius: 3,
                          p: 3,
                          textAlign: 'center',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
                          },
                        }}
                      >
                        <LinkedIn
                          sx={{
                            fontSize: 28,
                            color: selectedClient?.linkedin_connected
                              ? '#28a745'
                              : '#ffc107',
                            mb: 1.5,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: selectedClient?.linkedin_connected
                              ? '#28a745'
                              : '#ffc107',
                            fontWeight: 600,
                            fontSize: '14px',
                          }}
                        >
                          {selectedClient?.linkedin_connected
                            ? 'LinkedIn Connected'
                            : 'LinkedIn Not Connected'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {form.client_id && getClientFacebookInfo(form.client_id) && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          background: 'rgba(24, 119, 242, 0.15)',
                          border: '2px solid #1877f2',
                          borderRadius: 3,
                          p: 3,
                          textAlign: 'center',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
                          },
                        }}
                      >
                        <Facebook
                          sx={{ fontSize: 28, color: '#1877f2', mb: 1.5 }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#1877f2',
                            fontWeight: 600,
                            fontSize: '14px',
                          }}
                        >
                          Facebook Page Connected:{' '}
                          {getClientFacebookInfo(form.client_id).pageName}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* YouTube Video Settings */}
              {(form.platforms || []).includes('youtube') && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    background: 'rgba(13, 17, 23, 0.4)',
                    border: '1px solid rgba(48, 54, 61, 0.3)',
                    borderRadius: 1.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#8b949e',
                      fontWeight: 500,
                      mb: 2,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '12px',
                    }}
                  >
                    YouTube Video Settings
                  </Typography>

                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    {/* First Row: Text Fields */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 200px' }, minWidth: { xs: 0, sm: '200px' } }}>
                        <TextField
                          name="youtubeTitle"
                          label="Video Title"
                          value={form.youtubeTitle || ''}
                          onChange={handleChange}
                          fullWidth
                          placeholder="Enter video title"
                          variant="outlined"
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'rgba(13, 17, 23, 0.8)',
                              '& fieldset': {
                                borderColor: 'rgba(48, 54, 61, 0.3)',
                              },
                              '&:hover fieldset': {
                                borderColor: 'rgba(88, 166, 255, 0.4)',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#58a6ff',
                              },
                            },
                          }}
                          InputProps={{
                            sx: {
                              color: '#f0f6fc',
                              fontSize: '14px',
                              '&::placeholder': {
                                color: '#6e7681',
                              },
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              color: '#8b949e',
                              fontSize: '14px',
                            },
                          }}
                        />
                      </Box>

                      <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                        <TextField
                          name="youtubeDescription"
                          label="Description"
                          value={form.youtubeDescription || ''}
                          onChange={handleChange}
                          fullWidth
                          multiline
                          rows={2}
                          placeholder="Video description"
                          variant="outlined"
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'rgba(13, 17, 23, 0.8)',
                              '& fieldset': {
                                borderColor: 'rgba(48, 54, 61, 0.3)',
                              },
                              '&:hover fieldset': {
                                borderColor: 'rgba(88, 166, 255, 0.4)',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#58a6ff',
                              },
                            },
                          }}
                          InputProps={{
                            sx: {
                              color: '#f0f6fc',
                              fontSize: '14px',
                              '&::placeholder': {
                                color: '#6e7681',
                              },
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              color: '#8b949e',
                              fontSize: '14px',
                            },
                          }}
                        />
                      </Box>

                      <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 200px' }, minWidth: { xs: 0, sm: '200px' } }}>
                        <TextField
                          name="youtubeTags"
                          label="Tags"
                          value={form.youtubeTags || ''}
                          onChange={handleChange}
                          fullWidth
                          placeholder="tag1, tag2, tag3"
                          variant="outlined"
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'rgba(13, 17, 23, 0.8)',
                              '& fieldset': {
                                borderColor: 'rgba(48, 54, 61, 0.3)',
                              },
                              '&:hover fieldset': {
                                borderColor: 'rgba(88, 166, 255, 0.4)',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#58a6ff',
                              },
                            },
                          }}
                          InputProps={{
                            sx: {
                              color: '#f0f6fc',
                              fontSize: '14px',
                              '&::placeholder': {
                                color: '#6e7681',
                              },
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              color: '#8b949e',
                              fontSize: '14px',
                            },
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Second Row: Dropdowns */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '0 0 150px', minWidth: '150px' }}>
                        <FormControl fullWidth size="small">
                          <InputLabel
                            sx={{ color: '#8b949e', fontSize: '14px' }}
                          >
                            Privacy
                          </InputLabel>
                          <Select
                            name="youtubePrivacy"
                            value={form.youtubePrivacy || 'public'}
                            onChange={handleChange}
                            label="Privacy"
                            sx={{
                              bgcolor: 'rgba(13, 17, 23, 0.8)',
                              color: '#f0f6fc',
                              fontSize: '14px',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(48, 54, 61, 0.3)',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(88, 166, 255, 0.4)',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                {
                                  borderColor: '#58a6ff',
                                },
                            }}
                          >
                            <MenuItem value="public" sx={{ fontSize: '14px' }}>
                              Public
                            </MenuItem>
                            <MenuItem
                              value="unlisted"
                              sx={{ fontSize: '14px' }}
                            >
                              Unlisted
                            </MenuItem>
                            <MenuItem value="private" sx={{ fontSize: '14px' }}>
                              Private
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 200px' }, minWidth: { xs: 0, sm: '200px' } }}>
                        <FormControl fullWidth size="small">
                          <InputLabel
                            sx={{ color: '#8b949e', fontSize: '14px' }}
                          >
                            Category
                          </InputLabel>
                          <Select
                            name="youtubeCategory"
                            value={form.youtubeCategory || '22'}
                            onChange={handleChange}
                            label="Category"
                            sx={{
                              bgcolor: 'rgba(13, 17, 23, 0.8)',
                              color: '#f0f6fc',
                              fontSize: '14px',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(48, 54, 61, 0.3)',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(88, 166, 255, 0.4)',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                {
                                  borderColor: '#58a6ff',
                                },
                            }}
                          >
                            <MenuItem value="22" sx={{ fontSize: '14px' }}>
                              People & Blogs
                            </MenuItem>
                            <MenuItem value="23" sx={{ fontSize: '14px' }}>
                              Comedy
                            </MenuItem>
                            <MenuItem value="24" sx={{ fontSize: '14px' }}>
                              Entertainment
                            </MenuItem>
                            <MenuItem value="25" sx={{ fontSize: '14px' }}>
                              News & Politics
                            </MenuItem>
                            <MenuItem value="26" sx={{ fontSize: '14px' }}>
                              Howto & Style
                            </MenuItem>
                            <MenuItem value="27" sx={{ fontSize: '14px' }}>
                              Education
                            </MenuItem>
                            <MenuItem value="28" sx={{ fontSize: '14px' }}>
                              Science & Technology
                            </MenuItem>
                            <MenuItem value="10" sx={{ fontSize: '14px' }}>
                              Music
                            </MenuItem>
                            <MenuItem value="15" sx={{ fontSize: '14px' }}>
                              Pets & Animals
                            </MenuItem>
                            <MenuItem value="17" sx={{ fontSize: '14px' }}>
                              Sports
                            </MenuItem>
                            <MenuItem value="19" sx={{ fontSize: '14px' }}>
                              Travel & Events
                            </MenuItem>
                            <MenuItem value="20" sx={{ fontSize: '14px' }}>
                              Gaming
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                        <FormControl fullWidth size="small">
                          <InputLabel
                            sx={{ color: '#8b949e', fontSize: '14px' }}
                          >
                            Playlists
                          </InputLabel>
                          <Select
                            name="youtubePlaylists"
                            multiple
                            value={form.youtubePlaylists || []}
                            onChange={handleChange}
                            label="Playlists"
                            renderValue={(selected) => (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 0.5,
                                }}
                              >
                                {selected.length === 0 ? (
                                  <Typography
                                    sx={{ color: '#6e7681', fontSize: '14px' }}
                                  >
                                    Select playlists
                                  </Typography>
                                ) : (
                                  selected.map((value) => {
                                    const playlist = youtubePlaylists.find(
                                      (p) => p.id === value,
                                    );
                                    return (
                                      <Chip
                                        key={value}
                                        label={
                                          playlist ? playlist.title : value
                                        }
                                        size="small"
                                        sx={{
                                          bgcolor: 'rgba(88, 166, 255, 0.15)',
                                          color: '#58a6ff',
                                          border:
                                            '1px solid rgba(88, 166, 255, 0.2)',
                                          fontSize: '12px',
                                          height: '24px',
                                        }}
                                      />
                                    );
                                  })
                                )}
                              </Box>
                            )}
                            sx={{
                              bgcolor: 'rgba(13, 17, 23, 0.8)',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(48, 54, 61, 0.3)',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(88, 166, 255, 0.4)',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                {
                                  borderColor: '#58a6ff',
                                },
                            }}
                          >
                            {youtubePlaylists.length > 0 ? (
                              youtubePlaylists.map((playlist) => (
                                <MenuItem
                                  key={playlist.id}
                                  value={playlist.id}
                                  sx={{ fontSize: '14px' }}
                                >
                                  <ListItemText
                                    primary={playlist.title}
                                    secondary={`${playlist.itemCount} videos`}
                                    primaryTypographyProps={{
                                      fontSize: '14px',
                                    }}
                                    secondaryTypographyProps={{
                                      fontSize: '12px',
                                    }}
                                  />
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem
                                value=""
                                disabled
                                sx={{ fontSize: '14px' }}
                              >
                                {youtubePlaylists.length === 0
                                  ? 'Loading playlists...'
                                  : 'No playlists found'}
                              </MenuItem>
                            )}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions
              sx={{
                p: 4,
                borderTop: '1px solid rgba(48, 54, 61, 0.4)',
                gap: 2,
                justifyContent: 'flex-end',
                background: 'rgba(13, 17, 23, 0.5)',
              }}
            >
              <Button
                type="button"
                onClick={handleCloseForm}
                sx={{
                  px: 4,
                  py: 2,
                  borderRadius: 3,
                  color: '#9ca3af',
                  borderColor: '#374151',
                  border: '1px solid',
                  fontSize: '14px',
                  fontWeight: 600,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: '#58a6ff',
                    bgcolor: 'rgba(88, 166, 255, 0.1)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  px: 4,
                  py: 2,
                  borderRadius: 3,
                  bgcolor: '#58a6ff',
                  fontSize: '14px',
                  fontWeight: 600,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: '#1f6feb',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 8px 25px rgba(88, 166, 255, 0.3)',
                  },
                }}
              >
                {editingId ? 'Update' : 'Create'}
              </Button>
              <Button
                type="button"
                variant="outlined"
                sx={{
                  px: 4,
                  py: 2,
                  borderRadius: 3,
                  color: '#28a745',
                  borderColor: '#28a745',
                  border: '1px solid',
                  fontSize: '14px',
                  fontWeight: 600,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: '#20c997',
                    bgcolor: 'rgba(40, 167, 69, 0.1)',
                    transform: 'translateY(-1px)',
                  },
                }}
                onClick={(e) => {
                  e.preventDefault();
                  handleSchedule('new', true);
                }}
              >
                Post Now
              </Button>
              <Button
                type="button"
                variant="outlined"
                sx={{
                  px: 4,
                  py: 2,
                  borderRadius: 3,
                  color: '#ff9800',
                  borderColor: '#ff9800',
                  border: '1px solid',
                  fontSize: '14px',
                  fontWeight: 600,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: '#f57c00',
                    bgcolor: 'rgba(255, 152, 0, 0.1)',
                    transform: 'translateY(-1px)',
                  },
                }}
                onClick={(e) => {
                  e.preventDefault();
                  const errors = validateForm();
                  if (Object.keys(errors).length > 0) {
                    setFormErrors(errors);
                    setSnackbar({
                      open: true,
                      message: 'Please fix form errors before scheduling.',
                      severity: 'error',
                    });
                    return;
                  }
                  setSchedulingId(editingId || 'new');
                }}
              >
                Schedule
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* View Post Details Dialog */}
        <Dialog
          open={!!viewPost}
          onClose={handleCloseView}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Post Details</DialogTitle>
          <DialogContent>
            {viewPost && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {viewPost.title || 'Untitled Post'}
                </Typography>
                <Typography variant="body1" paragraph>
                  {viewPost.content}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2">
                      <b>Client:</b> {viewPost.client?.name || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2">
                      <b>Platform:</b> {viewPost.platform}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2">
                      <b>Content Type:</b> {viewPost.content_type}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2">
                      <b>Status:</b> {viewPost.status}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Typography variant="subtitle2">
                        <b>Publish Status:</b>
                      </Typography>
                      <PublishStatusChip post={viewPost} />
                    </Box>
                  </Grid>
                  {viewPost.group_share && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                        Group share
                      </Typography>
                      <GroupShareStatus post={viewPost} />
                    </Grid>
                  )}
                  {viewPost.scheduled_time && (
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">
                        <b>Scheduled:</b> {formatToEST(viewPost.scheduled_time)}
                      </Typography>
                    </Grid>
                  )}
                  {viewPost.published_time && (
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">
                        <b>Published:</b> {formatToEST(viewPost.published_time)}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                      Engagement
                    </Typography>
                    <EngagementSummary post={viewPost} />
                  </Grid>
                  {viewPost.audit && (
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ mt: 1, color: 'text.secondary' }}
                      >
                        Source and timeline
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6} sm={4}>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Caption draft ID
                          </Typography>
                          <Typography variant="body2">
                            {viewPost.audit.draft_id != null
                              ? viewPost.audit.draft_id
                              : '—'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Variant index
                          </Typography>
                          <Typography variant="body2">
                            {viewPost.audit.variant_index != null
                              ? viewPost.audit.variant_index
                              : '—'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Scheduled (UTC→EST)
                          </Typography>
                          <Typography variant="body2">
                            {viewPost.audit.scheduled_time
                              ? formatToEST(viewPost.audit.scheduled_time)
                              : '—'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Published (UTC→EST)
                          </Typography>
                          <Typography variant="body2">
                            {viewPost.audit.published_time
                              ? formatToEST(viewPost.audit.published_time)
                              : '—'}
                          </Typography>
                        </Grid>
                        {viewPost.audit.failure_reason && (
                          <Grid item xs={12}>
                            <Typography variant="caption" display="block" color="error">
                              Publish failure
                            </Typography>
                            <Typography variant="body2" color="error">
                              {viewPost.audit.failure_reason}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  )}
                  {viewPost.hashtags && viewPost.hashtags.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        <b>Hashtags:</b>
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {viewPost.hashtags.map((tag, index) => (
                          <Chip key={index} label={tag} size="small" />
                        ))}
                      </Box>
                    </Grid>
                  )}
                  {viewPost.media_urls && viewPost.media_urls.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        <b>Media URLs:</b>
                      </Typography>
                      {viewPost.media_urls.map((url, index) => (
                        <Typography
                          key={index}
                          variant="body2"
                          color="text.secondary"
                        >
                          {url}
                        </Typography>
                      ))}
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseView}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Enhanced Schedule Post Dialog */}
        <Dialog
          open={!!schedulingId}
          onClose={() => setSchedulingId(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Schedule Post</DialogTitle>
          <DialogContent>
            <Box
              sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mt: 2 }}
            >
              <Box sx={{ flex: 1 }}>
                <DateTimePicker
                  label="Scheduled Time (EST)"
                  value={scheduledTime}
                  onChange={(date) => {
                    postsDebug('DateTimePicker onChange called with:', date);
                    setScheduledTime(date);
                  }}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  minDateTime={new Date()}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <FormControl fullWidth>
                  <InputLabel>Timezone</InputLabel>
                  <Select value="EST" label="Timezone" disabled>
                    <MenuItem value="EST">Eastern Standard Time (EST)</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSchedulingId(null)}>Cancel</Button>
            <Button
              onClick={() => {
                postsDebug(
                  'Schedule button clicked, schedulingId:',
                  schedulingId,
                );
                handleSchedule(schedulingId);
              }}
              variant="contained"
            >
              Schedule
            </Button>
          </DialogActions>
        </Dialog>

        {/* Post Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => {
              handleDelete(selectedPostForMenu?.id);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            Delete
          </MenuItem>
          {selectedPostForMenu?.status === 'scheduled' && (
            <MenuItem
              onClick={() => {
                handleReschedule(selectedPostForMenu);
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <Schedule fontSize="small" />
              </ListItemIcon>
              Reschedule
            </MenuItem>
          )}
          {selectedPostForMenu?.status === 'draft' && (
            <>
              <MenuItem
                onClick={() => {
                  setSchedulingId(selectedPostForMenu?.id);
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <Schedule fontSize="small" />
                </ListItemIcon>
                Schedule
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handlePublish(selectedPostForMenu?.id);
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <Publish fontSize="small" />
                </ListItemIcon>
                Publish Now
              </MenuItem>
            </>
          )}
          {selectedPostForMenu?.status === 'published' && (selectedPostForMenu?.platform || '').toLowerCase() === 'facebook' && (
            <MenuItem
              onClick={() => {
                handleShareToGroups(selectedPostForMenu);
                handleMenuClose();
              }}
              disabled={!!shareToGroupsPostId}
            >
              <ListItemIcon>
                <Share fontSize="small" />
              </ListItemIcon>
              Share to groups
            </MenuItem>
          )}
          <Divider />
          <MenuItem
            onClick={() => {
              handleCreateRecurring(selectedPostForMenu);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <Repeat fontSize="small" />
            </ListItemIcon>
            Create Recurring Schedule
          </MenuItem>
        </Menu>

        {/* Preview Dialog */}
        <Dialog
          open={previewDialog.open}
          onClose={() => setPreviewDialog({ open: false, post: null })}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Post Preview</DialogTitle>
          <DialogContent>
            {previewDialog.post && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    {previewDialog.post.title || 'Untitled Post'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <PostPreview
                    platform={
                      previewDialog.post.platform?.toLowerCase() || 'facebook'
                    }
                    content={previewDialog.post.content}
                    title={previewDialog.post.title}
                    media={previewDialog.post.media_urls?.[0]}
                    hashtags={previewDialog.post.hashtags || []}
                    user={{
                      name: previewDialog.post.client?.name || '',
                      handle: '',
                    }}
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setPreviewDialog({ open: false, post: null })}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reschedule Dialog */}
        <Dialog
          open={rescheduleDialog.open}
          onClose={() =>
            setRescheduleDialog({
              open: false,
              post: null,
              newTime: new Date(),
            })
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reschedule Post</DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <DateTimePicker
                  label="New Scheduled Time (EST)"
                  value={rescheduleDialog.newTime}
                  onChange={(date) =>
                    setRescheduleDialog((prev) => ({ ...prev, newTime: date }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} fullWidth margin="normal" />
                  )}
                  minDateTime={new Date()}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Timezone</InputLabel>
                  <Select value="EST" label="Timezone" disabled>
                    <MenuItem value="EST">Eastern Standard Time (EST)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                setRescheduleDialog({
                  open: false,
                  post: null,
                  newTime: new Date(),
                })
              }
            >
              Cancel
            </Button>
            <Button onClick={handleRescheduleSubmit} variant="contained">
              Reschedule
            </Button>
          </DialogActions>
        </Dialog>

        {/* Recurring Schedule Dialog */}
        <Dialog
          open={recurringDialog.open}
          onClose={() => setRecurringDialog({ open: false, post: null })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Create Recurring Schedule</DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={recurringConfig.frequency}
                    onChange={(e) =>
                      setRecurringConfig((prev) => ({
                        ...prev,
                        frequency: e.target.value,
                      }))
                    }
                    label="Frequency"
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Interval"
                  type="number"
                  value={recurringConfig.interval}
                  onChange={(e) =>
                    setRecurringConfig((prev) => ({
                      ...prev,
                      interval: parseInt(e.target.value),
                    }))
                  }
                  fullWidth
                  margin="normal"
                  inputProps={{ min: 1, max: 30 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Time of Day"
                  type="time"
                  value={recurringConfig.timeOfDay}
                  onChange={(e) =>
                    setRecurringConfig((prev) => ({
                      ...prev,
                      timeOfDay: e.target.value,
                    }))
                  }
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Timezone</InputLabel>
                  <Select
                    value={bulkTimezone}
                    onChange={(e) => setBulkTimezone(e.target.value)}
                    label="Timezone"
                  >
                    <MenuItem value="EST">Eastern Standard Time (EST)</MenuItem>
                    <MenuItem value="CST">Central Standard Time (CST)</MenuItem>
                    <MenuItem value="MST">
                      Mountain Standard Time (MST)
                    </MenuItem>
                    <MenuItem value="PST">Pacific Standard Time (PST)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {recurringConfig.frequency === 'weekly' && (
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Days of Week</InputLabel>
                    <Select
                      multiple
                      value={recurringConfig.daysOfWeek}
                      onChange={(e) =>
                        setRecurringConfig((prev) => ({
                          ...prev,
                          daysOfWeek: e.target.value,
                        }))
                      }
                      input={<OutlinedInput label="Days of Week" />}
                      renderValue={(selected) => selected.join(', ')}
                    >
                      {[
                        'Monday',
                        'Tuesday',
                        'Wednesday',
                        'Thursday',
                        'Friday',
                        'Saturday',
                        'Sunday',
                      ].map((day) => (
                        <MenuItem key={day} value={day}>
                          <Checkbox
                            checked={
                              recurringConfig.daysOfWeek.indexOf(day) > -1
                            }
                          />
                          <ListItemText primary={day} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  label="End Date (Optional)"
                  type="date"
                  value={recurringConfig.endDate || ''}
                  onChange={(e) =>
                    setRecurringConfig((prev) => ({
                      ...prev,
                      endDate: e.target.value,
                    }))
                  }
                  fullWidth
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setRecurringDialog({ open: false, post: null })}
            >
              Cancel
            </Button>
            <Button onClick={handleRecurringSubmit} variant="contained">
              Create Schedule
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Action Dialog */}
        <Dialog
          open={bulkActionDialog.open}
          onClose={() => setBulkActionDialog({ open: false, action: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Bulk Reschedule Posts</DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <DateTimePicker
                  label="New Scheduled Time (EST)"
                  value={bulkScheduledTime}
                  onChange={(date) => setBulkScheduledTime(date)}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth margin="normal" />
                  )}
                  minDateTime={new Date()}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Timezone</InputLabel>
                  <Select
                    value={bulkTimezone}
                    onChange={(e) => setBulkTimezone(e.target.value)}
                    label="Timezone"
                  >
                    <MenuItem value="EST">Eastern Standard Time (EST)</MenuItem>
                    <MenuItem value="CST">Central Standard Time (CST)</MenuItem>
                    <MenuItem value="MST">
                      Mountain Standard Time (MST)
                    </MenuItem>
                    <MenuItem value="PST">Pacific Standard Time (PST)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setBulkActionDialog({ open: false, action: null })}
            >
              Cancel
            </Button>
            <Button onClick={handleBulkReschedule} variant="contained">
              Reschedule All
            </Button>
          </DialogActions>
        </Dialog>

        <ConfirmDialog
          open={confirmDialog.open}
          title="Delete post"
          message={
            confirmDialog.data?.type === 'bulk'
              ? `Delete ${confirmDialog.data.ids.length} selected posts? This cannot be undone.`
              : 'Are you sure you want to delete this post? This action cannot be undone.'
          }
          confirmLabel="Delete"
          loading={Boolean(deletingId)}
          onCancel={() =>
            setConfirmDialog({ open: false, action: null, data: null })
          }
          onConfirm={handleConfirmAction}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <MuiAlert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>

        {/* Posting Progress Dialog */}
        <PostingProgressDialog
          open={postingProgress}
          onClose={() => {
            setPostingProgress(false);
            setPlatformStatus({});
            setSelectedPostForMenu(null);
          }}
          platforms={
            postingProgress && selectedPostForMenu
              ? selectedPostForMenu.platforms || []
              : []
          }
          status={platformStatus}
        />
      </Box>
    </LocalizationProvider>
  );
}
